import React, { useState, useEffect, useRef, useCallback } from "react";
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/img-redundant-alt */
/* eslint-disable no-unused-vars */

import { db } from "./firebase";
import {
  doc, setDoc, getDoc, collection,
  onSnapshot, updateDoc, deleteDoc
} from "firebase/firestore";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  navy:   "#0D1B3E", navyL: "#162040", navyD: "#060e1f",
  gold:   "#D4A017", goldL: "#F0C040",
  cream:  "#FDF6E3", green: "#27AE60", red: "#E74C3C",
  orange: "#E67E22", muted: "#7A8499", warn: "#FF6B35",
};
const F = { serif: "'Playfair Display', serif", mono: "monospace" };

const nowTime = () => new Date().toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit", hour12: false });
const todayStr = () => new Date().toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
const dateKey  = () => new Date().toISOString().slice(0, 10);
const uid      = () => Math.random().toString(36).slice(2, 9);

// ── Storage / cleanup helpers ─────────────────────────────────────────────────
const PHOTO_RETENTION_DAYS = 90;

function isPhotoExpired(dateStr) {
  const diffDays = (new Date() - new Date(dateStr + "T00:00:00")) / (1000*60*60*24);
  return diffDays > PHOTO_RETENTION_DAYS;
}

function purgeOldPhotos(children) {
  let purgedCount = 0;
  const cleaned = children.map(child => ({
    ...child,
    pickupHistory: child.pickupHistory.map(entry => {
      if (entry.photo && isPhotoExpired(entry.date)) {
        purgedCount++;
        return { ...entry, photo: null, photoExpired: true };
      }
      return entry;
    })
  }));
  return { cleaned, purgedCount };
}

function estimateStorageMB(children) {
  let bytes = 0;
  children.forEach(c => c.pickupHistory.forEach(h => {
    if (h.photo) bytes += h.photo.length * 0.75;
  }));
  return (bytes / (1024 * 1024)).toFixed(1);
}

function photoStats(children) {
  let total = 0, withPhoto = 0, expiringSoon = 0;
  const now = new Date();
  children.forEach(c => c.pickupHistory.forEach(h => {
    total++;
    if (h.photo) {
      withPhoto++;
      const days = (now - new Date(h.date + "T00:00:00")) / (1000*60*60*24);
      if (days > 60 && days <= 90) expiringSoon++;
    }
  }));
  return { total, withPhoto, expiringSoon };
}

// ── Seed data ─────────────────────────────────────────────────────────────────
const SEED = [
  {
    id: "c1", name: "Amahle Dlamini", pin: "1234", photo: "🧒",
    guardian: "Nomsa Dlamini", guardianPhone: "082 111 2222",
    emergencyContacts: [
      { name: "Sipho Dlamini", relation: "Father", phone: "083 222 3333" },
      { name: "Gogo Dlamini",  relation: "Grandmother", phone: "071 444 5555" },
    ],
    authorisedCollectors: [
      { name: "Sipho Dlamini",  relation: "Father",      phone: "083 222 3333", pin: "4321" },
      { name: "Gogo Dlamini",   relation: "Grandmother", phone: "071 444 5555", pin: "8888" },
    ],
    status: "at-school", latePickupTime: "17:00",
    activityLog: { [dateKey()]: ["Arrived safely", "Completed art activity"] },
    pickupHistory: [],
  },
  {
    id: "c2", name: "Sipho Khumalo", pin: "5678", photo: "👦",
    guardian: "Bongani Khumalo", guardianPhone: "083 333 4444",
    emergencyContacts: [{ name: "Lindiwe Khumalo", relation: "Mother", phone: "084 555 6666" }],
    authorisedCollectors: [{ name: "Lindiwe Khumalo", relation: "Mother", phone: "084 555 6666", pin: "8765" }],
    status: "at-school", latePickupTime: "16:30",
    activityLog: { [dateKey()]: ["Arrived safely", "Phonics session completed"] },
    pickupHistory: [],
  },
  {
    id: "c3", name: "Lerato Mokoena", pin: "9012", photo: "👧",
    guardian: "Thandi Mokoena", guardianPhone: "071 555 6666",
    emergencyContacts: [{ name: "Kabelo Mokoena", relation: "Uncle", phone: "060 777 8888" }],
    authorisedCollectors: [],
    status: "picked-up", latePickupTime: "17:30",
    activityLog: { [dateKey()]: ["Arrived safely"] },
    pickupHistory: [{ date: dateKey(), time: "13:45", collectedBy: "Thandi Mokoena", relation: "Guardian", event: "Picked up", photo: null }],
  },
];

const STAFF_PIN = "STAFF2026";

// ── Teacher seed data ─────────────────────────────────────────────────────────
const TEACHER_STATUSES = ["Present","Late","Absent","On Leave","Sick","Half Day"];
const STATUS_COLORS = {
  "Present":  "#27AE60",
  "Late":     "#FF6B35",
  "Absent":   "#E74C3C",
  "On Leave": "#4fc3f7",
  "Sick":     "#E67E22",
  "Half Day": "#D4A017",
};

const SEED_TEACHERS = [
  { id:"t1", name:"Ms Nomvula Dube",    role:"Grade R Teacher",    pin:"1111", emoji:"👩‍🏫", attendance:[] },
  { id:"t2", name:"Ms Priya Naidoo",    role:"ECD Practitioner",   pin:"2222", emoji:"👩‍🏫", attendance:[] },
  { id:"t3", name:"Mr Sipho Mahlangu",  role:"Assistant Teacher",  pin:"3333", emoji:"👨‍🏫", attendance:[] },
  { id:"t4", name:"Ms Thandi Cele",     role:"Admin & Reception",  pin:"4444", emoji:"👩‍💼", attendance:[] },
];

// ── WhatsApp helpers ──────────────────────────────────────────────────────────
function normPhone(raw) {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, "");
  if (digits.startsWith("27")) return digits;
  if (digits.startsWith("0"))  return "27" + digits.slice(1);
  return "27" + digits;
}

function waLink(phone, msg) {
  return `https://wa.me/${normPhone(phone)}?text=${encodeURIComponent(msg)}`;
}

function buildSchoolMsg({ child, collectedBy, relation, action, time, schoolName }) {
  const evt = action === "pickup" ? "🟢 PICKUP" : "🔵 DROP-OFF";
  return (
    `${evt} — ${schoolName}\n` +
    `──────────────────\n` +
    `⭐ *Nkanyezi SafePickup*\n` +
    `Child: *${child}*\n` +
    `${action === "pickup" ? "Collected" : "Returned"} by: *${collectedBy}* (${relation})\n` +
    `Time: *${time}*  |  Date: *${dateKey()}*\n` +
    `──────────────────\n` +
    `_Automated notification — Nkanyezi Digital Skills Academy_`
  );
}

function buildParentMsg({ child, collectedBy, relation, action, time, schoolName }) {
  const emoji = action === "pickup" ? "✅" : "🏫";
  return (
    `${emoji} *${schoolName} — SafePickup Update*\n\n` +
    `Dear Guardian,\n\n` +
    `This is to confirm that *${child}* was ${action === "pickup" ? "collected from school" : "returned to school"} ` +
    `by *${collectedBy}* (${relation}) at *${time}* today.\n\n` +
    `If you did NOT authorise this, please call the school immediately.\n\n` +
    `_Powered by Nkanyezi SafePickup_`
  );
}

const DEFAULT_SETTINGS = {
  schoolName:       "My School",
  schoolGroupLink:  "",
  schoolPhone:      "",
  panicPrincipal:   "",
  panicSecurity:    "",
  panicEmergency:   "",
  schoolStartTime:  "08:00",
  adminPin:         "STAFF2026",
};

// ── Panic alert helper ────────────────────────────────────────────────────────
function firePanicAlert(settings) {
  const msg =
    `🚨🚨🚨 *EMERGENCY ALERT*\n` +
    `──────────────────\n` +
    `School: *${settings.schoolName}*\n` +
    `Time: *${nowTime()}* · ${dateKey()}\n` +
    `──────────────────\n` +
    `⚠ A staff member has triggered the *SafePickup panic alert*.\n` +
    `Please call the school immediately or send help.\n\n` +
    `_Nkanyezi SafePickup Emergency System_`;

  const contacts = [
    settings.panicPrincipal,
    settings.panicSecurity,
    settings.panicEmergency
  ].filter(Boolean);

  contacts.forEach((phone, i) => {
    setTimeout(() => {
      window.open(waLink(phone, msg), "_blank");
    }, i * 700);
  });

  if (contacts.length === 0) {
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  }
}

// ── Reusable UI ───────────────────────────────────────────────────────────────
function Toast({ msg, type = "info", onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 4000); return () => clearTimeout(t); }, []);
  const bg = type === "warn" ? C.warn : type === "error" ? C.red : C.navy;
  return (
    <div style={{
      position: "fixed", bottom: 32, left: "50%", transform: "translateX(-50%)",
      background: bg, color: C.cream, fontFamily: F.serif, fontSize: 14,
      padding: "14px 28px", borderRadius: 14, boxShadow: "0 8px 40px rgba(0,0,0,0.5)",
      zIndex: 9999, border: `1.5px solid ${type === "warn" ? C.warn : C.gold}`,
      maxWidth: 420, textAlign: "center", animation: "fadeUp 0.3s ease"
    }}>{msg}</div>
  );
}

function Badge({ label, color }) {
  return (
    <span style={{
      background: color + "22", color, borderRadius: 20, padding: "3px 11px",
      fontSize: 11, fontWeight: 700, letterSpacing: 0.5, fontFamily: F.serif,
      border: `1px solid ${color}44`, whiteSpace: "nowrap"
    }}>{label}</span>
  );
}

function Btn({ children, onClick, variant = "primary", small, style: sx, disabled }) {
  const base = {
    fontFamily: F.serif, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
    border: "none", borderRadius: small ? 10 : 14, padding: small ? "7px 16px" : "13px 26px",
    fontSize: small ? 12 : 14, transition: "opacity 0.15s, transform 0.1s",
    opacity: disabled ? 0.5 : 1, ...sx
  };
  const v = {
    primary:   { background: C.gold,    color: C.navy,  boxShadow: `0 4px 16px ${C.gold}44` },
    secondary: { background: C.navyL,   color: C.cream, border: `1px solid ${C.gold}55` },
    danger:    { background: C.red+"22",color: C.red,   border: `1px solid ${C.red}44` },
    ghost:     { background: "transparent", color: C.muted, border: `1px solid ${C.muted}44` },
    green:     { background: C.green,   color: "#fff",  boxShadow: `0 4px 16px ${C.green}44` },
  };
  return (
    <button onClick={disabled ? undefined : onClick} style={{ ...base, ...v[variant] }}
      onMouseDown={e => !disabled && (e.currentTarget.style.transform = "scale(0.95)")}
      onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
    >{children}</button>
  );
}

function Card({ children, style: sx, gold }) {
  return (
    <div style={{
      background: C.navyL, borderRadius: 18,
      border: `1.5px solid ${gold ? C.gold + "55" : "#ffffff0a"}`,
      padding: "20px 22px", ...sx
    }}>{children}</div>
  );
}

function SectionTitle({ children }) {
  return <h3 style={{ color: C.gold, fontFamily: F.serif, fontSize: 12, textTransform: "uppercase", letterSpacing: 1.5, margin: "0 0 14px", fontWeight: 600 }}>{children}</h3>;
}

function Input({ label, value, onChange, type = "text", placeholder }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ color: C.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 5 }}>{label}</label>}
      <input type={type} value={value} placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        style={{
          width: "100%", padding: "10px 14px", borderRadius: 10,
          background: C.navy, border: `1px solid ${C.gold}33`,
          color: C.cream, fontFamily: F.serif, fontSize: 14, outline: "none", boxSizing: "border-box"
        }} />
    </div>
  );
}

const statusColor = s => ({ "at-school": C.gold, "picked-up": C.red, "arrived": C.green }[s] || C.muted);
const statusLabel = s => ({ "at-school": "In School", "picked-up": "Picked Up", "arrived": "Just Arrived" }[s] || s);
const isLate = c => {
  if (c.status === "picked-up" || !c.latePickupTime) return false;
  const [h, m] = c.latePickupTime.split(":").map(Number);
  const n = new Date(); return n.getHours() > h || (n.getHours() === h && n.getMinutes() >= m);
};

// ── CAMERA CAPTURE COMPONENT ──────────────────────────────────────────────────
function CameraCapture({ childName, collectorName, action, onCapture, onSkip }) {
  const canvasRef = useRef(null);
  const fileRef   = useRef(null);
  const videoRef  = useRef(null);
  const streamRef = useRef(null);

  const [mode, setMode]         = useState("upload");
  const [captured, setCaptured] = useState(null);
  const [camError, setCamError] = useState(false);

  const actionColor = action === "pickup" ? C.green : C.gold;
  const actionLabel = action === "pickup" ? "Pickup" : "Drop-off";

  useEffect(() => {
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  const applyWatermark = (sourceCanvas) => {
    const c = sourceCanvas;
    const ctx = c.getContext("2d");
    const barH = Math.max(48, c.height * 0.07);
    ctx.fillStyle = "rgba(6,14,31,0.82)";
    ctx.fillRect(0, c.height - barH, c.width, barH);
    const fs1 = Math.max(13, c.width / 38);
    ctx.fillStyle = "#D4A017";
    ctx.font = `bold ${fs1}px serif`;
    ctx.fillText("Nkanyezi SafePickup", 14, c.height - barH + fs1 + 4);
    const fs2 = Math.max(10, c.width / 52);
    ctx.fillStyle = "#FDF6E3cc";
    ctx.font = `${fs2}px serif`;
    ctx.fillText(`${childName} · ${action === "pickup" ? "Collected" : "Returned"} by ${collectorName} · ${nowTime()} ${dateKey()}`, 14, c.height - barH + fs1 + fs2 + 10);
    return c.toDataURL("image/jpeg", 0.88);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const c = canvasRef.current;
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        c.getContext("2d").drawImage(img, 0, 0);
        setCaptured(applyWatermark(c));
        setMode("preview");
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    setCamError(false);
    setMode("camera");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch(e) {
      setCamError(true);
    }
  };

  const shoot = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    c.width = v.videoWidth || 1280;
    c.height = v.videoHeight || 960;
    c.getContext("2d").drawImage(v, 0, 0);
    setCaptured(applyWatermark(c));
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    setMode("preview");
  };

  return (
    <div style={{ fontFamily: F.serif, textAlign: "center", maxWidth: 480, margin: "0 auto" }}>
      <div style={{ background: C.navyL, borderRadius: 14, padding: "14px 18px", marginBottom: 18, border: `1.5px solid ${actionColor}44` }}>
        <p style={{ color: C.muted, fontSize: 10, textTransform: "uppercase", letterSpacing: 1.5, margin: "0 0 5px" }}>
          {actionLabel} Photo Verification
        </p>
        <p style={{ color: C.cream, fontWeight: 700, fontSize: 15, margin: 0 }}>{childName}</p>
        <p style={{ color: C.muted, fontSize: 12, margin: "2px 0 0" }}>
          {action === "pickup" ? "Collected" : "Returned"} by {collectorName}
        </p>
      </div>

      {mode === "upload" && (
        <div>
          <div style={{ background: C.navyL, borderRadius: 16, padding: "28px 20px", border: `1.5px solid ${actionColor}44`, marginBottom: 16 }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>📷</div>
            <p style={{ color: C.cream, fontFamily: F.serif, fontWeight: 700, fontSize: 15, margin: "0 0 8px" }}>
              Take or Upload a Photo
            </p>
            <p style={{ color: C.muted, fontSize: 12, margin: "0 0 20px", lineHeight: 1.6 }}>
              Take a photo of the collector and child together for the pickup record.
            </p>
            <input ref={fileRef} type="file" accept="image/*" capture="environment"
              onChange={handleFileUpload} style={{ display: "none" }} />
            <button onClick={() => fileRef.current?.click()} style={{
              width: "100%", background: `linear-gradient(135deg, ${actionColor}, ${actionColor}cc)`,
              color: action === "pickup" ? "#fff" : C.navy,
              border: "none", borderRadius: 14, padding: "18px 0", fontSize: 16,
              fontFamily: F.serif, fontWeight: 700, cursor: "pointer",
              boxShadow: `0 6px 20px ${actionColor}44`, marginBottom: 12
            }}>
              📸 Take / Upload Photo
            </button>
            <Btn variant="ghost" onClick={onSkip} style={{ width: "100%" }}>
              Continue Without Photo →
            </Btn>
          </div>
        </div>
      )}

      {mode === "camera" && (
        <div>
          {camError ? (
            <div style={{ background: C.navyL, borderRadius: 14, padding: "20px", marginBottom: 16 }}>
              <p style={{ color: C.warn, fontSize: 14, fontWeight: 700, margin: "0 0 12px" }}>Camera unavailable</p>
              <Btn onClick={() => setMode("upload")}>← Use Upload Instead</Btn>
            </div>
          ) : (
            <div>
              <div style={{ borderRadius: 16, overflow: "hidden", background: "#000", marginBottom: 14, aspectRatio: "4/3" }}>
                <video ref={videoRef} autoPlay playsInline muted
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              </div>
              <div style={{ display: "flex", gap: 14, justifyContent: "center" }}>
                <button onClick={() => { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); setMode("upload"); }}
                  style={{ width: 48, height: 48, borderRadius: "50%", background: C.navyL, border: `1px solid ${C.muted}44`, fontSize: 16, cursor: "pointer", color: C.muted }}>✕</button>
                <button onClick={shoot}
                  style={{ width: 76, height: 76, borderRadius: "50%", border: `3px solid ${actionColor}`, background: actionColor, fontSize: 28, cursor: "pointer", boxShadow: `0 0 0 6px ${actionColor}33` }}>📸</button>
              </div>
            </div>
          )}
        </div>
      )}

      {mode === "preview" && captured && (
        <div>
          <div style={{ borderRadius: 16, overflow: "hidden", marginBottom: 14, border: `2px solid ${C.green}55` }}>
            <img src={captured} alt="Pickup" style={{ width: "100%", display: "block", borderRadius: 14 }} />
          </div>
          <p style={{ color: C.muted, fontSize: 11, marginBottom: 16 }}>Watermarked with name, time & date ✓</p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <Btn variant="green" onClick={() => onCapture(captured)}>✅ Use This Photo</Btn>
            <Btn variant="secondary" onClick={() => { setCaptured(null); setMode("upload"); }}>🔄 Retake</Btn>
            <Btn variant="ghost" onClick={onSkip}>Skip →</Btn>
          </div>
        </div>
      )}

      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
}

// ── PIN Pad ───────────────────────────────────────────────────────────────────
function PinPad({ label, hint, onSubmit, onBack, length = 4, alphanumeric = false }) {
  const [val, setVal] = useState("");
  const press = d => { if (val.length < (alphanumeric ? 12 : length)) setVal(v => v + d); };
  const del   = () => setVal(v => v.slice(0, -1));
  const sub   = () => val.length > 0 && onSubmit(val);

  return (
    <div style={{ textAlign: "center", fontFamily: F.serif }}>
      <p style={{ color: C.muted, fontSize: 13, marginBottom: 6, letterSpacing: 1, textTransform: "uppercase" }}>{label}</p>
      {hint && <p style={{ color: C.muted, fontSize: 11, marginBottom: 18 }}>{hint}</p>}
      {alphanumeric ? (
        <input value={val} onChange={e => setVal(e.target.value.toUpperCase().slice(0, 12))}
          style={{
            padding: "12px 20px", borderRadius: 12, border: `1.5px solid ${C.gold}55`,
            background: C.navy, color: C.gold, fontSize: 22, fontFamily: F.mono,
            letterSpacing: 6, outline: "none", textAlign: "center", width: 220,
            display: "block", margin: "0 auto 20px"
          }} />
      ) : (
        <div style={{ display: "flex", gap: 12, justifyContent: "center", marginBottom: 24 }}>
          {Array.from({ length }).map((_, i) => (
            <div key={i} style={{
              width: 16, height: 16, borderRadius: "50%",
              background: i < val.length ? C.gold : "transparent",
              border: `2px solid ${i < val.length ? C.gold : C.muted}`,
              transition: "background 0.15s"
            }} />
          ))}
        </div>
      )}
      {!alphanumeric && [["1","2","3"],["4","5","6"],["7","8","9"],["⌫","0","✓"]].map((row, ri) => (
        <div key={ri} style={{ display: "flex", gap: 12, justifyContent: "center", marginBottom: 10 }}>
          {row.map(k => (
            <button key={k} onClick={() => k === "⌫" ? del() : k === "✓" ? sub() : press(k)}
              style={{
                width: 70, height: 70, borderRadius: 16,
                background: k === "✓" ? C.gold : k === "⌫" ? "#1a2a4a" : C.navyL,
                color: k === "✓" ? C.navy : C.cream,
                fontSize: k === "✓" || k === "⌫" ? 20 : 24,
                fontFamily: F.serif, fontWeight: 700, border: "none", cursor: "pointer",
                boxShadow: "0 4px 14px rgba(0,0,0,0.3)", transition: "transform 0.1s"
              }}
              onMouseDown={e => e.currentTarget.style.transform = "scale(0.9)"}
              onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
            >{k}</button>
          ))}
        </div>
      ))}
      {alphanumeric && <div style={{ marginTop: 10 }}><Btn onClick={sub}>Submit →</Btn></div>}
      {onBack && <div style={{ marginTop: 20 }}><Btn variant="ghost" onClick={onBack}>← Back</Btn></div>}
    </div>
  );
}

// ── Child Modal (staff) ───────────────────────────────────────────────────────
// KEY FIX: onUpdateChild is passed as a prop and used directly in the Add Collector button
function ChildModal({ child, onClose, onAddActivity, onUpdateChild }) {
  const [tab, setTab] = useState("info");
  const [note, setNote] = useState("");
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [ecForm, setEcForm] = useState({ name:"", relation:"", phone:"" });
  const [medForm, setMedForm] = useState({
    medicalAidName: child.medicalInfo?.medicalAidName || "",
    medicalAidNumber: child.medicalInfo?.medicalAidNumber || "",
    memberNumber: child.medicalInfo?.memberNumber || "",
    allergies: child.medicalInfo?.allergies || "",
    chronicMedication: child.medicalInfo?.chronicMedication || "",
    doctorName: child.medicalInfo?.doctorName || "",
    doctorPhone: child.medicalInfo?.doctorPhone || "",
    bloodType: child.medicalInfo?.bloodType || "",
  });
  const [medSaved, setMedSaved] = useState(false);
  const late = isLate(child);
  const tabs = ["info","collectors","emergency","activity","history","edit"];
  const tabLabel = t => ({ info:"Overview", collectors:"Collectors", emergency:"Emergency", activity:"Activity Log", history:"Pickup Photos", edit:"✏️ Edit" }[t]);
  const addAct = () => { if (!note.trim()) return; onAddActivity(child.id, note.trim()); setNote(""); };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(6,14,31,0.88)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={onClose}>
      {lightboxPhoto && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.95)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setLightboxPhoto(null)}>
          <img src={lightboxPhoto} alt="Pickup photo" style={{ maxWidth: "90vw", maxHeight: "85vh", borderRadius: 14, boxShadow: "0 24px 80px rgba(0,0,0,0.9)" }} />
          <p style={{ position: "absolute", bottom: 24, color: C.muted, fontFamily: F.serif, fontSize: 13 }}>Tap anywhere to close</p>
        </div>
      )}

      <div style={{ background: C.navy, borderRadius: 22, width: "100%", maxWidth: 640, border: `2px solid ${C.gold}44`, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 80px rgba(0,0,0,0.6)" }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding: "24px 28px 0", borderBottom: `1px solid ${C.gold}22` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              <span style={{ fontSize: 48 }}>{child.photo}</span>
              <div>
                <h2 style={{ color: C.cream, fontFamily: F.serif, margin: 0, fontSize: 20 }}>{child.name}</h2>
                <p style={{ color: C.muted, fontFamily: F.serif, margin: "4px 0 8px", fontSize: 13 }}>{child.guardian} · {child.guardianPhone}</p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Badge label={statusLabel(child.status)} color={statusColor(child.status)} />
                  {late && <Badge label="⚠ LATE" color={C.warn} />}
                </div>
              </div>
            </div>
            <Btn variant="ghost" small onClick={onClose}>✕</Btn>
          </div>
          <div style={{ display: "flex", gap: 4, overflowX: "auto" }}>
            {tabs.map(t => (
              <button key={t} onClick={() => setTab(t)} style={{
                background: tab === t ? C.gold : "transparent", color: tab === t ? C.navy : C.muted,
                border: "none", borderRadius: "10px 10px 0 0", padding: "8px 14px",
                fontFamily: F.serif, fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap"
              }}>{tabLabel(t)}</button>
            ))}
          </div>
        </div>

        <div style={{ padding: "22px 28px" }}>
          {tab === "info" && (
            <div>
              {late && <div style={{ background: C.warn+"22", border: `1.5px solid ${C.warn}`, borderRadius: 12, padding: "12px 16px", marginBottom: 16 }}>
                <p style={{ color: C.warn, fontFamily: F.serif, margin: 0, fontWeight: 700, fontSize: 13 }}>⚠ Late Pickup — expected by {child.latePickupTime}</p>
              </div>}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <Card><SectionTitle>Pickup Deadline</SectionTitle>
                  <p style={{ color: C.cream, fontFamily: F.serif, fontSize: 24, fontWeight: 700, margin: 0 }}>{child.latePickupTime}</p>
                </Card>
                <Card><SectionTitle>Total Pickups</SectionTitle>
                  <p style={{ color: C.cream, fontFamily: F.serif, fontSize: 24, fontWeight: 700, margin: 0 }}>{child.pickupHistory.length}</p>
                  <p style={{ color: C.muted, fontSize: 12, fontFamily: F.serif, margin: "4px 0 0" }}>
                    {child.pickupHistory.filter(h => h.photo).length} with photo
                  </p>
                </Card>
              </div>
              <Card style={{ marginTop: 14 }}>
                <SectionTitle>Today's Activity</SectionTitle>
                {(child.activityLog[dateKey()] || []).length === 0
                  ? <p style={{ color: C.muted, fontFamily: F.serif, fontSize: 13 }}>No activities logged.</p>
                  : (child.activityLog[dateKey()] || []).map((a, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                      <span style={{ color: C.gold, fontSize: 10, marginTop: 3 }}>●</span>
                      <span style={{ color: C.cream, fontFamily: F.serif, fontSize: 13 }}>{a}</span>
                    </div>
                  ))
                }
              </Card>
            </div>
          )}

          {tab === "collectors" && (
            <div>
              <Card gold style={{ marginBottom: 14 }}>
                <SectionTitle>Primary Guardian</SectionTitle>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <p style={{ color: C.cream, fontFamily: F.serif, fontWeight: 700, margin: 0, fontSize: 15 }}>{child.guardian}</p>
                    <p style={{ color: C.muted, fontFamily: F.serif, fontSize: 13, margin: "3px 0 0" }}>{child.guardianPhone}</p>
                  </div>
                  <Badge label="Primary" color={C.gold} />
                </div>
              </Card>

              {(child.authorisedCollectors || []).length === 0
                ? <p style={{ color: C.muted, fontFamily: F.serif, fontSize: 13, marginBottom: 16 }}>No additional collectors.</p>
                : (child.authorisedCollectors || []).map((c, i) => (
                  <Card key={i} style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <p style={{ color: C.cream, fontFamily: F.serif, fontWeight: 700, margin: 0, fontSize: 14 }}>{c.name}</p>
                        <p style={{ color: C.muted, fontFamily: F.serif, fontSize: 12, margin: "3px 0 0" }}>{c.relation} · {c.phone}</p>
                        <p style={{ color: C.gold, fontFamily: F.mono, fontSize: 12, margin: "3px 0 0", letterSpacing: 2 }}>PIN: {c.pin}</p>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                        <Badge label="Authorised" color={C.green} />
                        <Btn small variant="danger" onClick={() => {
                          const updated = {
                            ...child,
                            authorisedCollectors: (child.authorisedCollectors || []).filter((_, j) => j !== i)
                          };
                          onUpdateChild(updated);
                        }}>✕ Remove</Btn>
                      </div>
                    </div>
                  </Card>
                ))
              }

              {/* ── ADD COLLECTOR BUTTON ── */}
              <Btn variant="green" style={{ width: "100%", marginTop: 12 }} onClick={async () => {
                const name = window.prompt("Collector's full name:");
                if (!name) return;
                const relation = window.prompt("Relationship to child (e.g. Aunt, Uncle, Grandmother):");
                if (!relation) return;
                const phone = window.prompt("Collector's phone number (e.g. 082 111 2222):");
                if (!phone) return;
                const pin = String(Math.floor(1000 + Math.random() * 9000));
                const newCollector = { name, relation, phone, pin };
                const updatedChild = {
                  ...child,
                  authorisedCollectors: [...(child.authorisedCollectors || []), newCollector]
                };
                // Save to Firebase first, then send WhatsApp
                await onUpdateChild(updatedChild);
                const msg = `Hello ${name}! 👋 You have been added as an authorised collector for *${child.name}*. Your secure collector PIN is: *${pin}*. You will need this PIN every time you collect the child. Do not share it with anyone. — Nkanyezi SafePickup 🔒`;
                setTimeout(() => window.open(`https://wa.me/${normPhone(phone)}?text=${encodeURIComponent(msg)}`, "_blank"), 500);
              }}>+ Add Collector</Btn>
            </div>
          )}

          {tab === "emergency" && (
            <div>
              {/* Emergency Contacts */}
              <SectionTitle>🆘 Emergency Contacts</SectionTitle>
              {(child.emergencyContacts || []).length === 0
                ? <p style={{ color: C.muted, fontFamily: F.serif, fontSize: 13, marginBottom: 16 }}>No emergency contacts added yet.</p>
                : (child.emergencyContacts || []).map((c, i) => (
                  <Card key={i} style={{ marginBottom: 12, border: `1.5px solid ${C.red}33` }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div>
                        <p style={{ color: C.cream, fontFamily: F.serif, fontWeight: 700, margin: "0 0 4px", fontSize: 15 }}>🆘 {c.name}</p>
                        <p style={{ color: C.muted, fontFamily: F.serif, fontSize: 13, margin: 0 }}>{c.relation}</p>
                        <p style={{ color: C.gold, fontFamily: F.mono, fontSize: 14, margin: "4px 0 0", letterSpacing: 1 }}>{c.phone}</p>
                      </div>
                      <Btn small variant="danger" onClick={() => {
                        const updated = { ...child, emergencyContacts: (child.emergencyContacts||[]).filter((_,j)=>j!==i) };
                        onUpdateChild(updated);
                      }}>✕ Remove</Btn>
                    </div>
                  </Card>
                ))
              }
              {/* Add Emergency Contact */}
              <div style={{ background: C.navyL, borderRadius: 12, padding:"16px", marginBottom:20, border:`1px solid ${C.red}33` }}>
                <p style={{ color:C.muted, fontFamily:F.serif, fontSize:11, textTransform:"uppercase", letterSpacing:1, margin:"0 0 12px" }}>+ Add Emergency Contact</p>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
                  <input placeholder="Full Name" value={ecForm.name} onChange={e=>setEcForm(f=>({...f,name:e.target.value}))}
                    style={{ padding:"9px 12px", borderRadius:8, background:C.navy, border:`1px solid ${C.gold}33`, color:C.cream, fontFamily:F.serif, fontSize:13, outline:"none" }} />
                  <input placeholder="Relation (e.g. Father)" value={ecForm.relation} onChange={e=>setEcForm(f=>({...f,relation:e.target.value}))}
                    style={{ padding:"9px 12px", borderRadius:8, background:C.navy, border:`1px solid ${C.gold}33`, color:C.cream, fontFamily:F.serif, fontSize:13, outline:"none" }} />
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <input placeholder="Phone Number" value={ecForm.phone} onChange={e=>setEcForm(f=>({...f,phone:e.target.value}))}
                    style={{ flex:1, padding:"9px 12px", borderRadius:8, background:C.navy, border:`1px solid ${C.gold}33`, color:C.cream, fontFamily:F.serif, fontSize:13, outline:"none" }} />
                  <Btn small variant="danger" onClick={() => {
                    if (!ecForm.name||!ecForm.phone) return;
                    const updated = { ...child, emergencyContacts: [...(child.emergencyContacts||[]), {...ecForm}] };
                    onUpdateChild(updated);
                    setEcForm({name:"",relation:"",phone:""});
                  }}>+ Add</Btn>
                </div>
              </div>

              {/* Medical Information */}
              <SectionTitle>🏥 Medical Information</SectionTitle>
              <div style={{ background: C.navyL, borderRadius:14, padding:"16px", border:`1px solid ${C.gold}22`, marginBottom:16 }}>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                  {[
                    ["medicalAidName","Medical Aid Name","e.g. Discovery"],
                    ["medicalAidNumber","Medical Aid Number","e.g. 12345678"],
                    ["memberNumber","Member Number","e.g. 98765432"],
                    ["bloodType","Blood Type","e.g. O+"],
                    ["doctorName","Doctor's Name","e.g. Dr Nkosi"],
                    ["doctorPhone","Doctor's Phone","e.g. 011 000 1111"],
                  ].map(([k, label, ph]) => (
                    <div key={k}>
                      <label style={{ color:C.muted, fontSize:10, textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:4 }}>{label}</label>
                      <input value={medForm[k]} onChange={e=>setMedForm(f=>({...f,[k]:e.target.value}))} placeholder={ph}
                        style={{ width:"100%", padding:"8px 10px", borderRadius:8, background:C.navy, border:`1px solid ${C.gold}33`, color:C.cream, fontFamily:F.serif, fontSize:12, outline:"none", boxSizing:"border-box" }} />
                    </div>
                  ))}
                </div>
                {[
                  ["allergies","Known Allergies","e.g. Penicillin, Peanuts, Bee stings"],
                  ["chronicMedication","Chronic Medication","e.g. Ventolin inhaler for asthma"],
                ].map(([k, label, ph]) => (
                  <div key={k} style={{ marginBottom:10 }}>
                    <label style={{ color:C.muted, fontSize:10, textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:4 }}>{label}</label>
                    <input value={medForm[k]} onChange={e=>setMedForm(f=>({...f,[k]:e.target.value}))} placeholder={ph}
                      style={{ width:"100%", padding:"8px 10px", borderRadius:8, background:C.navy, border:`1px solid ${C.gold}33`, color:C.cream, fontFamily:F.serif, fontSize:12, outline:"none", boxSizing:"border-box" }} />
                  </div>
                ))}
                {medSaved && (
                  <div style={{ background:C.green+"22", borderRadius:8, padding:"8px 12px", marginBottom:10 }}>
                    <p style={{ color:C.green, fontFamily:F.serif, fontSize:12, fontWeight:700, margin:0 }}>✅ Medical info saved!</p>
                  </div>
                )}
                <Btn variant="green" onClick={() => {
                  const updated = { ...child, medicalInfo: { ...medForm } };
                  onUpdateChild(updated);
                  setMedSaved(true);
                  setTimeout(()=>setMedSaved(false), 2500);
                }} style={{ width:"100%" }}>💾 Save Medical Info</Btn>
              </div>

              {/* Display saved medical info */}
              {child.medicalInfo && Object.values(child.medicalInfo).some(v=>v) && (
                <div style={{ background: C.red+"11", borderRadius:12, padding:"14px 16px", border:`1.5px solid ${C.red}33` }}>
                  <p style={{ color:C.red, fontFamily:F.serif, fontWeight:700, fontSize:12, textTransform:"uppercase", letterSpacing:1, margin:"0 0 10px" }}>Current Medical Profile</p>
                  {[
                    ["🩸 Blood Type", child.medicalInfo.bloodType],
                    ["🏥 Medical Aid", child.medicalInfo.medicalAidName],
                    ["🔢 Aid Number", child.medicalInfo.medicalAidNumber],
                    ["🔢 Member No", child.medicalInfo.memberNumber],
                    ["⚠️ Allergies", child.medicalInfo.allergies],
                    ["💊 Medication", child.medicalInfo.chronicMedication],
                    ["👨‍⚕️ Doctor", child.medicalInfo.doctorName],
                    ["📱 Doctor Phone", child.medicalInfo.doctorPhone],
                  ].filter(([,v])=>v).map(([label, val]) => (
                    <p key={label} style={{ color:C.cream, fontFamily:F.serif, fontSize:13, margin:"0 0 6px" }}>
                      <span style={{ color:C.muted }}>{label}:</span> <strong>{val}</strong>
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "activity" && (
            <div>
              <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
                <input value={note} onChange={e => setNote(e.target.value)} placeholder="Log an activity, meal, note..."
                  style={{ flex: 1, padding: "10px 14px", borderRadius: 10, background: C.navyL, border: `1px solid ${C.gold}33`, color: C.cream, fontFamily: F.serif, fontSize: 13, outline: "none" }}
                  onKeyDown={e => e.key === "Enter" && addAct()} />
                <Btn onClick={addAct}>+ Add</Btn>
              </div>
              {Object.entries(child.activityLog).sort((a,b) => b[0].localeCompare(a[0])).map(([date, logs]) => (
                <div key={date} style={{ marginBottom: 20 }}>
                  <p style={{ color: C.gold, fontFamily: F.serif, fontSize: 12, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
                    {new Date(date+"T00:00:00").toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "long" })}
                  </p>
                  {logs.map((a, i) => (
                    <div key={i} style={{ display: "flex", gap: 12, marginBottom: 8 }}>
                      <span style={{ color: C.gold, fontSize: 8, marginTop: 5 }}>◆</span>
                      <span style={{ color: C.cream, fontFamily: F.serif, fontSize: 13 }}>{a}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {tab === "history" && (
            <div>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
                <Btn small variant="secondary" onClick={() => {
                  const rows = ["Date,Time,Event,Collected By,Relation,Has Photo"];
                  child.pickupHistory.forEach(h => rows.push(`${h.date},${h.time},${h.event},${h.collectedBy},${h.relation},${h.photo ? "Yes" : "No"}`));
                  const blob = new Blob([rows.join("\n")], { type: "text/csv" });
                  const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
                  a.download = `${child.name.replace(/ /g,"_")}_history.csv`; a.click();
                }}>⬇ Export CSV</Btn>
              </div>

              {child.pickupHistory.length === 0
                ? <p style={{ color: C.muted, fontFamily: F.serif, fontSize: 13 }}>No pickup history yet.</p>
                : [...child.pickupHistory].reverse().map((h, i) => (
                  <div key={i} style={{
                    display: "grid", gridTemplateColumns: h.photo ? "80px 1fr" : "1fr",
                    gap: 14, padding: "16px 0", borderBottom: `1px solid ${C.gold}18`,
                    alignItems: "center"
                  }}>
                    {h.photo && (
                      <div onClick={() => setLightboxPhoto(h.photo)} style={{ cursor: "zoom-in" }}>
                        <img src={h.photo} alt="Pickup" style={{
                          width: 76, height: 56, objectFit: "cover", borderRadius: 10,
                          border: `2px solid ${C.gold}44`, display: "block"
                        }} />
                        <p style={{ color: C.gold, fontSize: 9, fontFamily: F.serif, margin: "4px 0 0", textAlign: "center" }}>Tap to expand</p>
                      </div>
                    )}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <p style={{ color: C.cream, fontFamily: F.serif, fontWeight: 700, margin: 0, fontSize: 14 }}>{h.event}</p>
                        <p style={{ color: C.muted, fontFamily: F.serif, fontSize: 12, margin: "3px 0 0" }}>By: {h.collectedBy} ({h.relation})</p>
                        {!h.photo && <Badge label="No photo" color={C.muted} />}
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <p style={{ color: C.gold, fontFamily: F.mono, fontSize: 13, margin: 0 }}>{h.time}</p>
                        <p style={{ color: C.muted, fontFamily: F.serif, fontSize: 11, margin: "3px 0 0" }}>{h.date}</p>
                      </div>
                    </div>
                  </div>
                ))
              }
            </div>
          )}

          {tab === "edit" && (
            <div style={{ padding: "8px 0" }}>
              <p style={{ color: C.muted, fontFamily: F.serif, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, marginBottom: 16 }}>Child Information</p>
              <p style={{ color: C.cream, fontFamily: F.serif, fontSize: 13, margin: "0 0 8px" }}>👶 Name: <strong>{child.name}</strong></p>
              <p style={{ color: C.cream, fontFamily: F.serif, fontSize: 13, margin: "0 0 8px" }}>👤 Guardian: <strong>{child.guardian}</strong></p>
              <p style={{ color: C.cream, fontFamily: F.serif, fontSize: 13, margin: "0 0 8px" }}>📱 Phone: <strong>{child.guardianPhone || "Not set"}</strong></p>
              <p style={{ color: C.cream, fontFamily: F.serif, fontSize: 13, margin: "0 0 8px" }}>⏰ Late Pickup: <strong>{child.latePickupTime}</strong></p>
              <div style={{ background: C.navyL, borderRadius: 12, padding: "16px", margin: "16px 0", textAlign: "center", border: `1.5px solid ${C.gold}44` }}>
                <p style={{ color: C.muted, fontFamily: F.serif, fontSize: 11, margin: "0 0 4px" }}>CHILD PICKUP PIN</p>
                <p style={{ color: C.gold, fontFamily: F.mono, fontSize: 32, fontWeight: 700, letterSpacing: 8, margin: 0 }}>{child.pin}</p>
              </div>
              <p style={{ color: C.muted, fontFamily: F.serif, fontSize: 12, marginBottom: 16 }}>
                Share this PIN with the parent/guardian. They enter it at pickup.
              </p>
              <SectionTitle>Authorised Collectors & PINs</SectionTitle>
              {(child.authorisedCollectors || []).length === 0
                ? <p style={{ color: C.muted, fontFamily: F.serif, fontSize: 13 }}>No collectors added yet. Go to the Collectors tab to add them.</p>
                : (child.authorisedCollectors || []).map((c, i) => (
                  <div key={i} style={{ background: C.navyL, borderRadius: 12, padding: "12px 16px", marginBottom: 10, border: `1px solid ${C.gold}22` }}>
                    <p style={{ color: C.cream, fontFamily: F.serif, fontWeight: 700, fontSize: 13, margin: "0 0 4px" }}>{c.name} — {c.relation}</p>
                    <p style={{ color: C.muted, fontFamily: F.serif, fontSize: 12, margin: "0 0 4px" }}>{c.phone}</p>
                    <p style={{ color: C.gold, fontFamily: F.mono, fontSize: 16, fontWeight: 700, letterSpacing: 4, margin: 0 }}>PIN: {c.pin}</p>
                  </div>
                ))
              }
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Staff Dashboard ───────────────────────────────────────────────────────────
function StaffDutyTab({ teachers, today, onUpdateTeacher, settings }) {
  const [markingId, setMarkingId] = useState(null);

  const staffIn     = (teachers||[]).filter(t => { const r=t.attendance.find(a=>a.date===today); return r&&r.arrivalTime&&!r.departureTime; });
  const staffOut    = (teachers||[]).filter(t => { const r=t.attendance.find(a=>a.date===today); return r&&r.departureTime; });
  const staffAbsent = (teachers||[]).filter(t => { const r=t.attendance.find(a=>a.date===today); return r&&(r.status==="Absent"||r.status==="Sick"||r.status==="On Leave"||r.status==="Half Day"); });
  const staffNotIn  = (teachers||[]).filter(t => !t.attendance.find(a=>a.date===today));

  const SBadge = ({ status }) => (
    <span style={{
      background:(STATUS_COLORS[status]||C.muted)+"22", color:STATUS_COLORS[status]||C.muted,
      borderRadius:20, padding:"3px 10px", fontSize:11, fontWeight:700, fontFamily:F.serif,
      border:`1px solid ${(STATUS_COLORS[status]||C.muted)}44`, whiteSpace:"nowrap"
    }}>{status}</span>
  );

  const markStatus = (teacher, status) => {
    const existing = teacher.attendance.find(a => a.date === today);
    const entry = existing
      ? { ...existing, status }
      : { date: today, arrivalTime: null, departureTime: null, status };
    const updated = {
      ...teacher,
      attendance: [...teacher.attendance.filter(a => a.date !== today), entry]
    };
    onUpdateTeacher(updated);
    setMarkingId(null);
  };

  const markFuture = (teacher, date, status) => {
    const entry = { date, arrivalTime: null, departureTime: null, status, loggedBy: "principal" };
    const updated = {
      ...teacher,
      attendance: [...teacher.attendance.filter(a => a.date !== date), entry]
    };
    onUpdateTeacher(updated);
    setMarkingId(null);
  };

  const MarkModal = ({ teacher }) => {
    const [view, setView] = useState("today");
    const [futureDate, setFutureDate] = useState(() => {
      const d = new Date(); d.setDate(d.getDate()+1);
      return d.toISOString().slice(0,10);
    });
    const [futureStatus, setFutureStatus] = useState("On Leave");

    return (
      <div style={{
        position:"fixed", inset:0, background:"#00000088", zIndex:9998,
        display:"flex", alignItems:"center", justifyContent:"center", padding:24
      }} onClick={()=>setMarkingId(null)}>
        <div onClick={e=>e.stopPropagation()} style={{
          background:C.navyL, borderRadius:20, padding:28, width:"100%", maxWidth:360,
          border:`2px solid ${C.gold}44`, fontFamily:F.serif
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
            <span style={{ fontSize:28 }}>{teacher.emoji}</span>
            <div>
              <p style={{ color:C.cream, fontWeight:700, fontSize:15, margin:0 }}>{teacher.name}</p>
              <p style={{ color:C.muted, fontSize:11, margin:"2px 0 0" }}>{teacher.role}</p>
            </div>
          </div>

          <div style={{ display:"flex", gap:8, marginBottom:20 }}>
            {[["today","Today"],["future","Future Date"]].map(([k,l])=>(
              <button key={k} onClick={()=>setView(k)} style={{
                flex:1, background:view===k?C.gold:C.navy, color:view===k?C.navy:C.muted,
                border:"none", borderRadius:12, padding:"9px 0",
                fontFamily:F.serif, fontWeight:700, fontSize:13, cursor:"pointer"
              }}>{l}</button>
            ))}
          </div>

          {view === "today" && (
            <div>
              <p style={{ color:C.muted, fontSize:12, marginBottom:14 }}>Mark today's status for this teacher:</p>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {["Absent","Sick","On Leave","Half Day"].map(s=>(
                  <button key={s} onClick={()=>markStatus(teacher, s)} style={{
                    background:(STATUS_COLORS[s]||C.muted)+"22", color:STATUS_COLORS[s]||C.muted,
                    border:`1.5px solid ${(STATUS_COLORS[s]||C.muted)}55`,
                    borderRadius:12, padding:"13px 0",
                    fontFamily:F.serif, fontWeight:700, fontSize:14, cursor:"pointer"
                  }}>{s}</button>
                ))}
              </div>
              <div style={{ marginTop:12 }}><Btn variant="ghost" small onClick={()=>setMarkingId(null)}>Cancel</Btn></div>
            </div>
          )}

          {view === "future" && (
            <div>
              <p style={{ color:C.muted, fontSize:12, marginBottom:14 }}>Log planned leave for a future date:</p>
              <div style={{ marginBottom:14 }}>
                <label style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:6 }}>Date</label>
                <input type="date" value={futureDate}
                  min={new Date().toISOString().slice(0,10)}
                  onChange={e=>setFutureDate(e.target.value)}
                  style={{ width:"100%", padding:"10px 14px", borderRadius:10, background:C.navy, border:`1px solid ${C.gold}33`, color:C.gold, fontFamily:F.mono, fontSize:14, outline:"none", boxSizing:"border-box" }}
                />
              </div>
              <div style={{ marginBottom:16 }}>
                <label style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:6 }}>Reason</label>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {["On Leave","Sick","Half Day","Absent"].map(s=>(
                    <button key={s} onClick={()=>setFutureStatus(s)} style={{
                      background:futureStatus===s?(STATUS_COLORS[s]||C.muted)+"44":(STATUS_COLORS[s]||C.muted)+"11",
                      color:STATUS_COLORS[s]||C.muted,
                      border:`1.5px solid ${futureStatus===s?(STATUS_COLORS[s]||C.muted):"transparent"}`,
                      borderRadius:10, padding:"10px 0",
                      fontFamily:F.serif, fontWeight:700, fontSize:13, cursor:"pointer"
                    }}>{s}</button>
                  ))}
                </div>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <Btn onClick={()=>markFuture(teacher, futureDate, futureStatus)} style={{ flex:1 }}>Save Planned Leave</Btn>
                <Btn variant="ghost" onClick={()=>setMarkingId(null)}>Cancel</Btn>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:20 }}>
        {[
          { label:"In School",   val:staffIn.length,     color:C.green },
          { label:"Checked Out", val:staffOut.length,    color:C.gold  },
          { label:"Absent/Leave",val:staffAbsent.length, color:C.red   },
          { label:"Not Logged",  val:staffNotIn.length,  color:C.muted },
        ].map(s=>(
          <div key={s.label} style={{ background:C.navyL, borderRadius:12, padding:"12px 8px", textAlign:"center", border:`1px solid ${s.color}22` }}>
            <p style={{ color:s.color, fontFamily:F.serif, fontSize:22, fontWeight:700, margin:0 }}>{s.val}</p>
            <p style={{ color:C.muted, fontSize:10, textTransform:"uppercase", letterSpacing:1, margin:"3px 0 0" }}>{s.label}</p>
          </div>
        ))}
      </div>

      <div style={{ background:C.gold+"18", border:`1px solid ${C.gold}44`, borderRadius:12, padding:"10px 14px", marginBottom:16 }}>
        <p style={{ color:C.gold, fontFamily:F.serif, fontSize:12, margin:0 }}>
          👑 As principal you can mark any teacher's status. Tap a teacher card to update their attendance.
        </p>
      </div>

      {(teachers||[]).map(t => {
        const rec = t.attendance.find(a=>a.date===today);
        const statusCol = rec ? STATUS_COLORS[rec.status]||C.muted : C.muted;
        const upcoming = t.attendance
          .filter(a => a.date > today && a.loggedBy === "principal")
          .sort((a,b) => a.date.localeCompare(b.date))
          .slice(0,1)[0];

        return (
          <div key={t.id} style={{
            background:C.navyL, borderRadius:16, padding:"16px 18px", marginBottom:12,
            border:`1.5px solid ${statusCol}33`, cursor:"pointer"
          }} onClick={()=>setMarkingId(t.id)}>
            <div style={{ display:"grid", gridTemplateColumns:"auto 1fr auto", gap:14, alignItems:"center" }}>
              <span style={{ fontSize:30 }}>{t.emoji}</span>
              <div>
                <p style={{ color:C.cream, fontFamily:F.serif, fontWeight:700, fontSize:14, margin:0 }}>{t.name}</p>
                <p style={{ color:C.muted, fontFamily:F.serif, fontSize:11, margin:"3px 0 0" }}>{t.role}</p>
                {rec ? (
                  <p style={{ color:C.muted, fontFamily:F.mono, fontSize:11, margin:"5px 0 0" }}>
                    {rec.arrivalTime
                      ? <>In: <span style={{color:C.green}}>{rec.arrivalTime}</span>{rec.departureTime ? <> · Out: <span style={{color:C.gold}}>{rec.departureTime}</span></> : " · In school"}</>
                      : <span style={{color:STATUS_COLORS[rec.status]||C.muted}}>Logged by principal</span>
                    }
                  </p>
                ) : (
                  <p style={{ color:C.muted, fontFamily:F.serif, fontSize:11, margin:"5px 0 0" }}>Not logged — tap to mark</p>
                )}
                {upcoming && (
                  <p style={{ color:"#4fc3f7", fontFamily:F.serif, fontSize:10, margin:"5px 0 0" }}>
                    📅 {upcoming.status} planned: {new Date(upcoming.date+"T00:00:00").toLocaleDateString("en-ZA",{weekday:"short",day:"numeric",month:"short"})}
                  </p>
                )}
              </div>
              <div style={{ textAlign:"right", display:"flex", flexDirection:"column", gap:6, alignItems:"flex-end" }}>
                {rec
                  ? <span style={{ background:(STATUS_COLORS[rec.status]||C.muted)+"22", color:STATUS_COLORS[rec.status]||C.muted, borderRadius:20, padding:"3px 10px", fontSize:11, fontWeight:700, fontFamily:F.serif }}>{rec.status}</span>
                  : <span style={{ color:C.gold, fontFamily:F.serif, fontSize:11 }}>Tap ✏️</span>
                }
              </div>
            </div>
          </div>
        );
      })}

      {(teachers||[]).length === 0 && (
        <div style={{ background:C.navyL, borderRadius:14, padding:"24px", textAlign:"center" }}>
          <p style={{ color:C.muted, fontFamily:F.serif, fontSize:13, margin:0 }}>No teachers added yet. Go to Admin → Teachers to add staff.</p>
        </div>
      )}

      {markingId && (() => {
        const t = (teachers||[]).find(t=>t.id===markingId);
        return t ? <MarkModal teacher={t} /> : null;
      })()}
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function Dashboard({ children, teachers, onLogout, onAdminClick, onAddActivity, onUpdateChild, onUpdateTeacher, settings }) {
  const [selected, setSelected] = useState(null);
  const [filter, setFilter]     = useState("all");
  const [tab, setTab]           = useState("children");
  const today    = dateKey();
  const lateKids = children.filter(isLate);
  const displayed = children.filter(c =>
    filter === "all" ? true : filter === "school" ? c.status !== "picked-up" :
    filter === "pickedup" ? c.status === "picked-up" : isLate(c)
  );

  const staffIn      = (teachers||[]).filter(t => { const r=t.attendance.find(a=>a.date===today); return r&&r.arrivalTime&&!r.departureTime; });
  const staffAbsent  = (teachers||[]).filter(t => { const r=t.attendance.find(a=>a.date===today); return r&&(r.status==="Absent"||r.status==="Sick"); });
  const staffNotIn   = (teachers||[]).filter(t => !t.attendance.find(a=>a.date===today));
  const staffLate    = (teachers||[]).filter(t => { const r=t.attendance.find(a=>a.date===today); return r&&r.status==="Late"; });

  return (
    <div style={{ padding: "22px 24px", fontFamily: F.serif }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 26, cursor:"default" }} onClick={()=>window.__nkanyeziStarTap?.()}>⭐</span>
            <h1 style={{ color: C.gold, fontSize: 20, fontWeight: 700, margin: 0 }}>Nkanyezi SafePickup</h1>
          </div>
          <p style={{ color: C.muted, fontSize: 12, margin: "4px 0 0 36px" }}>{todayStr()}</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant="secondary" small onClick={onAdminClick}>Admin</Btn>
          <Btn variant="ghost" small onClick={onLogout}>Log Out</Btn>
        </div>
      </div>

      {lateKids.length > 0 && (
        <div style={{ background: C.warn+"22", border: `2px solid ${C.warn}`, borderRadius: 14, padding: "12px 18px", marginBottom: 16, display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ fontSize: 22 }}>⚠️</span>
          <div>
            <p style={{ color: C.warn, fontFamily: F.serif, fontWeight: 700, margin: 0, fontSize: 13 }}>Late Pickup — {lateKids.length} child{lateKids.length > 1 ? "ren" : ""}</p>
            <p style={{ color: C.cream, fontFamily: F.serif, fontSize: 11, margin: "3px 0 0" }}>{lateKids.map(c => c.name.split(" ")[0]).join(", ")}</p>
          </div>
        </div>
      )}

      {(staffAbsent.length > 0 || staffLate.length > 0) && (
        <div style={{ background: C.orange+"18", border: `1.5px solid ${C.orange}55`, borderRadius: 14, padding: "12px 18px", marginBottom: 16, display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ fontSize: 22 }}>👩‍🏫</span>
          <div>
            {staffAbsent.length > 0 && <p style={{ color: C.red, fontFamily: F.serif, fontWeight: 700, margin: "0 0 2px", fontSize: 13 }}>Absent/Sick: {staffAbsent.map(t=>t.name.split(" ")[0]).join(", ")}</p>}
            {staffLate.length > 0  && <p style={{ color: C.warn, fontFamily: F.serif, fontWeight: 700, margin: 0, fontSize: 13 }}>Late arrival: {staffLate.map(t=>t.name.split(" ")[0]).join(", ")}</p>}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 18 }}>
        {[
          { label: "In School",    val: children.filter(c=>c.status!=="picked-up").length, color: C.green,  bg: "#0a2518" },
          { label: "Picked Up",    val: children.filter(c=>c.status==="picked-up").length, color: C.red,    bg: "#2a0e0e" },
          { label: "Staff In",     val: staffIn.length,   color: C.gold,   bg: "#1a1400" },
          { label: "Staff Absent", val: staffAbsent.length, color: C.orange, bg: "#2a1200" },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, borderRadius: 14, padding: "14px 12px", border: `1px solid ${s.color}22` }}>
            <p style={{ color: C.muted, fontSize: 10, textTransform: "uppercase", letterSpacing: 1, margin: 0 }}>{s.label}</p>
            <p style={{ color: s.color, fontSize: 28, fontWeight: 700, margin: "4px 0 0", fontFamily: F.serif }}>{s.val}</p>
          </div>
        ))}
      </div>

      {(() => {
        const mb     = parseFloat(estimateStorageMB(children));
        const stats  = photoStats(children);
        const pct    = Math.min(100, Math.round((mb / 500) * 100));
        const barCol = pct > 80 ? C.red : pct > 50 ? C.warn : C.green;
        return (
          <div style={{ background: C.navyL, borderRadius: 14, padding: "12px 16px", marginBottom: 18, border: `1px solid ${barCol}33` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 13 }}>💾</span>
                <p style={{ color: C.cream, fontFamily: F.serif, fontSize: 12, fontWeight: 700, margin: 0 }}>Photo Storage</p>
                <Badge label={`${mb} MB`} color={barCol} />
                {stats.expiringSoon > 0 && <Badge label={`${stats.expiringSoon} expiring`} color={C.warn} />}
              </div>
              <p style={{ color: C.muted, fontFamily: F.serif, fontSize: 10, margin: 0 }}>{stats.withPhoto} photos · 90-day auto-delete</p>
            </div>
            <div style={{ height: 5, background: "#ffffff14", borderRadius: 5, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.max(pct, 2)}%`, background: `linear-gradient(90deg, ${barCol}88, ${barCol})`, borderRadius: 5 }} />
            </div>
          </div>
        );
      })()}

      <div style={{ display: "flex", gap: 4, marginBottom: 18, borderBottom: `1px solid ${C.gold}22` }}>
        {[["children","Children"],["staff","Staff on Duty"],["premises","On Premises"]].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            background:"transparent", color: tab===k ? C.gold : C.muted,
            border:"none", borderBottom: tab===k ? `3px solid ${C.gold}` : "3px solid transparent",
            padding:"10px 18px", fontFamily: F.serif, fontSize: 13, fontWeight: 700, cursor: "pointer"
          }}>{l}{k==="staff" && staffNotIn.length > 0 ? ` (${staffNotIn.length} not logged)` : ""}</button>
        ))}
      </div>

      {tab === "children" && (
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap:"wrap" }}>
            {[["all","All"],["school","In School"],["pickedup","Picked Up"],["late","Late ⚠"]].map(([k,l]) => (
              <button key={k} onClick={() => setFilter(k)} style={{
                background: filter===k ? C.gold : C.navyL, color: filter===k ? C.navy : C.muted,
                border: "none", borderRadius: 20, padding: "6px 14px", fontFamily: F.serif, fontSize: 12, fontWeight: 600, cursor: "pointer"
              }}>{l}</button>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 12 }}>
            {displayed.map(child => (
              <div key={child.id} onClick={() => setSelected(child)} style={{
                background: C.navyL, borderRadius: 16, padding: "14px 16px",
                border: `1.5px solid ${isLate(child) ? C.warn : statusColor(child.status)}33`,
                cursor: "pointer", transition: "transform 0.15s"
              }}
              onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
              onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 28 }}>{child.photo}</span>
                  <div style={{ flex: 1 }}>
                    <p style={{ color: C.cream, fontWeight: 700, fontSize: 13, margin: 0, fontFamily: F.serif }}>{child.name}</p>
                    <p style={{ color: C.muted, fontSize: 11, margin: "2px 0 0", fontFamily: F.serif }}>{child.guardian}</p>
                  </div>
                  {child.pickupHistory.some(h => h.photo) && <span style={{ fontSize: 14 }}>📸</span>}
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <Badge label={statusLabel(child.status)} color={statusColor(child.status)} />
                  {isLate(child) && <Badge label="Late!" color={C.warn} />}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "staff" && (
        <StaffDutyTab teachers={teachers} today={today} onUpdateTeacher={onUpdateTeacher} settings={settings} />
      )}

      {tab === "premises" && (
        <div>
          <h3 style={{ color:C.gold, fontFamily:F.serif, fontSize:15, margin:"0 0 16px" }}>
            On Premises — {new Date().toLocaleDateString("en-ZA",{weekday:"long",day:"numeric",month:"long"})}
          </h3>

          <SectionTitle>👤 Visitors On Premises</SectionTitle>
          {(() => {
            const visitors = (() => { try { return JSON.parse(localStorage.getItem("nkanyezi-visitors")||"[]"); } catch { return []; } })();
            const onPremises = visitors.filter(v => v.date === dateKey());
            if (onPremises.length === 0) return <p style={{ color:C.muted, fontFamily:F.serif, fontSize:13, marginBottom:20 }}>No visitors currently on premises.</p>;
            return onPremises.map(v => (
              <div key={v.id} style={{ background:C.navyL, borderRadius:12, padding:"12px 16px", marginBottom:10, border:`1.5px solid ${v.flagged ? C.red : C.gold}33` }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div>
                    <p style={{ color:C.cream, fontFamily:F.serif, fontWeight:700, fontSize:13, margin:0 }}>{v.name}</p>
                    <p style={{ color:C.muted, fontFamily:F.serif, fontSize:11, margin:"3px 0 0" }}>
                      {v.reason} · {v.checkedOut ? <span style={{color:C.green}}>✅ Departed: {v.checkoutTime}</span> : <span style={{color:C.warn}}>🟡 On Premises since {v.time}</span>}
                    </p>
                  </div>
                  {v.flagged && <Badge label="🚨 FLAGGED" color={C.red} />}
                </div>
              </div>
            ));
          })()}

          <SectionTitle>📅 Today's Appointments</SectionTitle>
          {(() => {
            const appts = (() => { try { return JSON.parse(localStorage.getItem("nkanyezi-appointments")||"[]"); } catch { return []; } })();
            const todays = appts.filter(a => a.date === dateKey());
            if (todays.length === 0) return <p style={{ color:C.muted, fontFamily:F.serif, fontSize:13 }}>No appointments scheduled today.</p>;
            return todays.map(a => (
              <div key={a.id} style={{ background:C.navyL, borderRadius:12, padding:"12px 16px", marginBottom:10, border:`1.5px solid ${C.gold}22` }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div>
                    <p style={{ color:C.cream, fontFamily:F.serif, fontWeight:700, fontSize:13, margin:0 }}>{a.with}</p>
                    <p style={{ color:C.muted, fontFamily:F.serif, fontSize:11, margin:"3px 0 0" }}>{a.purpose} · {a.time}</p>
                  </div>
                  <Badge label={a.status} color={a.status==="arrived" ? C.gold : a.status==="complete" ? C.green : C.muted} />
                </div>
              </div>
            ));
          })()}
        </div>
      )}

      {/* ── CHILD MODAL — KEY FIX: passes onUpdateChild directly ── */}
      {selected && (
        <ChildModal
          child={children.find(c => c.id === selected.id) || selected}
          onClose={() => setSelected(null)}
          onAddActivity={(id, note) => { onAddActivity(id, note); setSelected(s => s ? { ...s } : null); }}
          onUpdateChild={async (updatedChild) => {
            await onUpdateChild(updatedChild);
            setSelected(updatedChild);
          }}
        />
      )}
    </div>
  );
}

// ── WhatsApp Notification Step ────────────────────────────────────────────────
function WhatsAppStep({ child, collectedBy, relation, action, capturedPhoto, settings, onDone }) {
  const time = nowTime();
  const schoolMsg  = buildSchoolMsg({ child: child.name, collectedBy, relation, action, time, schoolName: settings.schoolName });
  const parentMsg  = buildParentMsg({ child: child.name, collectedBy, relation, action, time, schoolName: settings.schoolName });
  const parentPhone = child.guardianPhone;

  const hasGroupLink  = !!settings.schoolGroupLink;
  const hasSchoolPhone= !!settings.schoolPhone;
  const hasParentPhone= !!parentPhone;

  const [sentSchool,  setSentSchool]  = useState(false);
  const [sentParent,  setSentParent]  = useState(false);

  const openGroupWA = () => {
    const groupUrl = settings.schoolGroupLink.startsWith("http") ? settings.schoolGroupLink : `https://${settings.schoolGroupLink}`;
    window.open(groupUrl, "_blank");
    setSentSchool(true);
  };

  const openSchoolWA = () => {
    window.open(waLink(settings.schoolPhone, schoolMsg), "_blank");
    setSentSchool(true);
  };

  const openParentWA = () => {
    window.open(waLink(parentPhone, parentMsg), "_blank");
    setSentParent(true);
  };

  const actionColor = action === "pickup" ? C.green : C.gold;
  const allSent = sentSchool && sentParent;

  return (
    <div style={{ fontFamily: F.serif, textAlign: "center", maxWidth: 460, margin: "0 auto" }}>
      <div style={{ fontSize: 52, marginBottom: 10 }}>📲</div>
      <h3 style={{ color: C.gold, fontFamily: F.serif, fontSize: 20, margin: "0 0 6px" }}>Send WhatsApp Notifications</h3>
      <p style={{ color: C.muted, fontSize: 12, marginBottom: 24 }}>Tap each button below — WhatsApp opens with the message ready to send</p>

      <div style={{ background: C.navyL, borderRadius: 16, padding: "16px 20px", marginBottom: 24, border: `1.5px solid ${actionColor}44`, textAlign: "left" }}>
        <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 12 }}>
          <span style={{ fontSize: 36 }}>{child.photo}</span>
          <div>
            <p style={{ color: C.cream, fontWeight: 700, fontSize: 15, margin: 0 }}>{child.name}</p>
            <p style={{ color: C.muted, fontSize: 12, margin: "3px 0 0" }}>
              {action === "pickup" ? "Collected" : "Dropped off"} by {collectedBy} ({relation}) at {time}
            </p>
          </div>
          <Badge label={action === "pickup" ? "✅ Picked Up" : "🏫 Dropped off"} color={actionColor} />
        </div>
        {capturedPhoto && (
          <img src={capturedPhoto} alt="Pickup" style={{ width: "100%", borderRadius: 10, border: `1px solid ${C.gold}33`, display: "block" }} />
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 24 }}>
        <div style={{ background: sentSchool ? C.green + "18" : C.navyL, borderRadius: 16, padding: "16px 20px", border: `1.5px solid ${sentSchool ? C.green : C.gold}44`, textAlign: "left" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <div>
              <p style={{ color: sentSchool ? C.green : C.gold, fontWeight: 700, fontSize: 14, margin: 0 }}>
                {sentSchool ? "✅" : "💬"} School Group / Admin
              </p>
              <p style={{ color: C.muted, fontSize: 11, margin: "3px 0 0" }}>
                {hasGroupLink ? settings.schoolGroupLink.slice(0,36)+"…" : hasSchoolPhone ? settings.schoolPhone : "No school number set"}
              </p>
            </div>
            {sentSchool && <Badge label="Sent ✓" color={C.green} />}
          </div>
          <div style={{ background: C.navy, borderRadius: 10, padding: "10px 12px", marginBottom: 12 }}>
            <pre style={{ color: C.cream, fontSize: 11, fontFamily: F.serif, margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{schoolMsg}</pre>
          </div>
          {hasGroupLink ? (
            <Btn onClick={openGroupWA} style={{ width: "100%" }}>🟢 Open WhatsApp Group</Btn>
          ) : hasSchoolPhone ? (
            <Btn onClick={openSchoolWA} style={{ width: "100%" }}>📱 Message School Admin</Btn>
          ) : (
            <Btn variant="ghost" onClick={() => setSentSchool(true)} style={{ width: "100%" }}>Skip (no school number configured)</Btn>
          )}
        </div>

        <div style={{ background: sentParent ? C.green + "18" : C.navyL, borderRadius: 16, padding: "16px 20px", border: `1.5px solid ${sentParent ? C.green : "#4fc3f7"}44`, textAlign: "left" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <div>
              <p style={{ color: sentParent ? C.green : "#4fc3f7", fontWeight: 700, fontSize: 14, margin: 0 }}>
                {sentParent ? "✅" : "👨‍👩‍👧"} Guardian — {child.guardian}
              </p>
              <p style={{ color: C.muted, fontSize: 11, margin: "3px 0 0" }}>
                {hasParentPhone ? parentPhone : "No phone number on record"}
              </p>
            </div>
            {sentParent && <Badge label="Sent ✓" color={C.green} />}
          </div>
          <div style={{ background: C.navy, borderRadius: 10, padding: "10px 12px", marginBottom: 12 }}>
            <pre style={{ color: C.cream, fontSize: 11, fontFamily: F.serif, margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{parentMsg}</pre>
          </div>
          {hasParentPhone ? (
            <Btn onClick={openParentWA} variant="secondary" style={{ width: "100%", border: `1.5px solid #4fc3f744` }}>
              📱 Send to {child.guardian} on WhatsApp
            </Btn>
          ) : (
            <Btn variant="ghost" onClick={() => setSentParent(true)} style={{ width: "100%" }}>Skip (no guardian number on record)</Btn>
          )}
        </div>
      </div>

      <Btn onClick={onDone} variant={allSent ? "green" : "ghost"} disabled={!allSent} style={{ width: "100%", padding: "16px 0", fontSize: 15 }}>
        ✅ All Notifications Sent — Done
      </Btn>
    </div>
  );
}

// ── Parent / Pickup Flow ──────────────────────────────────────────────────────
function ParentFlow({ children, onUpdate, onBack, onToast, settings }) {
  const [step, setStep]           = useState("who");
  const [who, setWho]             = useState(null);
  const [matched, setMatched]     = useState(null);
  const [collector, setCollector] = useState(null);
  const [action, setAction]       = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [savedChild, setSavedChild] = useState(null);
  const [error, setError]         = useState("");

  const personName     = who === "guardian" ? matched?.guardian     : collector?.name;
  const personRelation = who === "guardian" ? "Guardian"            : collector?.relation;

  const handleChildPin = pin => {
    const child = children.find(c => c.pin === pin);
    if (!child) { setError("❌ No child found with that PIN."); return; }
    setMatched(child); setError("");
    who === "guardian" ? setStep("confirm") : setStep("collector-pin");
  };

  const handleCollectorPin = pin => {
    const found = matched.authorisedCollectors.find(c => c.pin === pin);
    if (!found) { setError("❌ Collector PIN not recognised."); return; }
    setCollector(found); setError(""); setStep("confirm");
  };

  const handleUnauthorised = () => {
    onToast("🚨 SECURITY ALERT: Unauthorised pickup attempt. Staff notified immediately.", "warn");
    reset();
  };

  const finalise = (photo) => {
    setCapturedPhoto(photo);
    const entry = {
      date: dateKey(), time: nowTime(),
      event: action === "pickup" ? "Picked up" : "Dropped off",
      collectedBy: personName, relation: personRelation,
      photo: photo || null
    };
    const updated = {
      ...matched,
      status: action === "pickup" ? "picked-up" : "at-school",
      pickupHistory: [...matched.pickupHistory, entry]
    };
    onUpdate(updated);
    setSavedChild(updated);
    setStep("whatsapp");
  };

  const reset = () => {
    setStep("who"); setWho(null); setMatched(null); setCollector(null);
    setAction(null); setCapturedPhoto(null); setSavedChild(null); setError("");
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 460, textAlign: "center", fontFamily: F.serif }}>
        <span style={{ fontSize: 28 }}>⭐</span>
        <h2 style={{ color: C.gold, fontSize: 18, margin: "6px 0 4px" }}>Nkanyezi SafePickup</h2>
        <p style={{ color: C.muted, fontSize: 12, marginBottom: 28, letterSpacing: 1 }}>SECURE PARENT PORTAL</p>

        {step === "who" && (
          <div>
            <p style={{ color: C.cream, fontSize: 15, marginBottom: 22 }}>Who is collecting today?</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 300, margin: "0 auto" }}>
              <Btn onClick={() => { setWho("guardian"); setStep("child-pin"); }}>👨‍👩‍👧 I'm the Parent / Guardian</Btn>
              <Btn variant="secondary" onClick={() => { setWho("collector"); setStep("child-pin"); }}>👤 I'm an Authorised Collector</Btn>
              <div style={{ border: `1px solid ${C.red}44`, borderRadius: 14, padding: "12px 16px", marginTop: 8 }}>
                <p style={{ color: C.muted, fontSize: 12, margin: "0 0 10px" }}>Not on the authorised list?</p>
                <Btn variant="danger" onClick={handleUnauthorised}>🚨 I am not authorised</Btn>
              </div>
            </div>
            <div style={{ marginTop: 22 }}><Btn variant="ghost" onClick={onBack}>← Back to Home</Btn></div>
          </div>
        )}

        {step === "child-pin" && (
          <>
            <PinPad label="Enter child's 4-digit PIN"
              hint={who === "collector" ? "You will also need your collector PIN next" : undefined}
              onSubmit={handleChildPin} onBack={() => { setStep("who"); setError(""); }} />
            {error && <p style={{ color: C.red, marginTop: 14, fontSize: 13 }}>{error}</p>}
          </>
        )}

        {step === "collector-pin" && matched && (
          <>
            <div style={{ background: C.navyL, borderRadius: 14, padding: "14px 20px", marginBottom: 22, border: `1px solid ${C.gold}33` }}>
              <span style={{ fontSize: 36 }}>{matched.photo}</span>
              <p style={{ color: C.cream, fontWeight: 700, margin: "8px 0 0", fontFamily: F.serif }}>{matched.name}</p>
            </div>
            <PinPad label="Enter your collector PIN" onSubmit={handleCollectorPin} onBack={() => { setStep("child-pin"); setError(""); }} />
            {error && (
              <div style={{ marginTop: 14 }}>
                <p style={{ color: C.red, fontSize: 13, marginBottom: 10 }}>{error}</p>
                <Btn variant="danger" small onClick={handleUnauthorised}>🚨 Report unauthorised attempt</Btn>
              </div>
            )}
          </>
        )}

        {step === "confirm" && matched && (
          <div>
            <Card gold style={{ maxWidth: 340, margin: "0 auto 22px" }}>
              <span style={{ fontSize: 52, display: "block", marginBottom: 10 }}>{matched.photo}</span>
              <h3 style={{ color: C.cream, fontFamily: F.serif, fontSize: 20, margin: "0 0 6px" }}>{matched.name}</h3>
              <p style={{ color: C.muted, fontFamily: F.serif, fontSize: 13, margin: "0 0 10px" }}>
                {who === "guardian" ? matched.guardian : collector.name}
                {who === "collector" && <> · {collector.relation}</>}
              </p>
              <Badge label={statusLabel(matched.status)} color={statusColor(matched.status)} />
            </Card>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              {matched.status !== "picked-up" && <Btn variant="green" onClick={() => { setAction("pickup"); setStep("popia"); }}>📸 Confirm & Take Photo</Btn>}
              <Btn variant="ghost" onClick={reset}>← Cancel</Btn>
            </div>
          </div>
        )}

        {step === "popia" && matched && (
          <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
            <div style={{ maxWidth:340, width:"100%", textAlign:"center", fontFamily:F.serif }}>
              <span style={{ fontSize:40 }}>🔒</span>
              <h2 style={{ color:C.gold, fontSize:20, margin:"12px 0 6px" }}>Privacy Notice</h2>
              <p style={{ color:C.muted, fontSize:11, letterSpacing:1, marginBottom:20 }}>POPIA — Protection of Personal Information Act</p>
              <div style={{ background:C.navyL, borderRadius:16, padding:"20px", marginBottom:20, textAlign:"left" }}>
                <p style={{ color:C.cream, fontSize:13, marginBottom:12 }}>By proceeding, you consent to Nkanyezi SafePickup collecting:</p>
                <p style={{ color:C.muted, fontSize:12, marginBottom:8 }}>📸 A photo for identity verification</p>
                <p style={{ color:C.muted, fontSize:12, marginBottom:8 }}>👤 Your name and relationship to the child</p>
                <p style={{ color:C.muted, fontSize:12, marginBottom:8 }}>🕐 Date and time of collection</p>
                <p style={{ color:C.muted, fontSize:12, marginBottom:16 }}>This data is used solely for child safety and security purposes and is accessible only to authorised school staff.</p>
                <p style={{ color:C.muted, fontSize:11 }}>Data is retained for 90 days. You may request deletion by contacting the school.</p>
              </div>
              <Btn variant="green" onClick={() => setStep("camera")} style={{ width:"100%", marginBottom:12 }}>✅ I Consent — Proceed</Btn>
              <Btn variant="ghost" onClick={reset} style={{ width:"100%" }}>❌ I Do Not Consent — Cancel</Btn>
            </div>
          </div>
        )}

        {step === "camera" && matched && (
          <CameraCapture
            childName={matched.name}
            collectorName={personName}
            action={action}
            onCapture={photo => finalise(photo)}
            onSkip={() => finalise(null)}
          />
        )}

        {step === "whatsapp" && savedChild && (
          <WhatsAppStep
            child={savedChild}
            collectedBy={personName}
            relation={personRelation}
            action={action}
            capturedPhoto={capturedPhoto}
            settings={settings}
            onDone={() => { reset(); onBack(); }}
          />
        )}
      </div>
    </div>
  );
}

// ── Stats Tab ─────────────────────────────────────────────────────────────────
function StatsTab({ children, teachers, period, setPeriod }) {
  const today = new Date().toISOString().slice(0, 10);
  const getDateRange = () => {
    if (period === "today") return [today];
    if (period === "week") {
      const now = new Date(); const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      const mon = new Date(now); mon.setDate(diff);
      return Array.from({ length: 7 }, (_, i) => { const d = new Date(mon); d.setDate(mon.getDate() + i); return d.toISOString().slice(0, 10); });
    }
    if (period === "month") {
      const now = new Date(); const days = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      return Array.from({ length: days }, (_, i) => { const d = new Date(now.getFullYear(), now.getMonth(), i + 1); return d.toISOString().slice(0, 10); });
    }
    return [today];
  };
  const dates = getDateRange();
  const pickupsInPeriod = children.flatMap(c => (c.pickupHistory || []).filter(h => dates.includes(h.date)));
  const presentInPeriod = children.filter(c => dates.some(d => (c.activityLog?.[d] || []).some(a => a.includes("ATTENDANCE:present"))));
  const absentInPeriod = children.filter(c => dates.some(d => (c.activityLog?.[d] || []).some(a => a.includes("ATTENDANCE:absent"))));
  const pickupHours = {};
  pickupsInPeriod.forEach(h => { if (!h.time) return; const hour = h.time.split(":")[0]; pickupHours[hour] = (pickupHours[hour] || 0) + 1; });
  const maxPickups = Math.max(...Object.values(pickupHours), 1);
  const sortedHours = Object.entries(pickupHours).sort((a, b) => a[0].localeCompare(b[0]));
  const teacherRecords = teachers.flatMap(t => (t.attendance || []).filter(a => dates.includes(a.date)).map(a => ({ ...a, name: t.name })));
  const tPresent = teacherRecords.filter(r => r.status === "Present").length;
  const tLate = teacherRecords.filter(r => r.status === "Late").length;
  const tAbsent = teacherRecords.filter(r => r.status === "Absent" || r.status === "Sick").length;
  const tLeave = teacherRecords.filter(r => r.status === "On Leave" || r.status === "Half Day").length;
  let visitors = []; try { visitors = JSON.parse(localStorage.getItem("nkanyezi-visitors") || "[]"); } catch {}
  const visitorsInPeriod = visitors.filter(v => dates.includes(v.date));
  const flaggedVisitors = visitorsInPeriod.filter(v => v.flagged);
  const reasonCounts = {};
  visitorsInPeriod.forEach(v => { const r = v.reason || "Unknown"; reasonCounts[r] = (reasonCounts[r] || 0) + 1; });
  let appts = []; try { appts = JSON.parse(localStorage.getItem("nkanyezi-appointments") || "[]"); } catch {}
  const apptsInPeriod = appts.filter(a => dates.includes(a.date));
  const apptsCompleted = apptsInPeriod.filter(a => a.status === "complete").length;
  const StatCard = ({ label, val, sub, color, icon }) => (
    <div style={{ background: C.navyL, borderRadius: 14, padding: "16px 14px", border: `1px solid ${color}22`, textAlign: "center" }}>
      <div style={{ fontSize: 22, marginBottom: 4 }}>{icon}</div>
      <p style={{ color, fontFamily: F.serif, fontSize: 26, fontWeight: 700, margin: 0 }}>{val}</p>
      <p style={{ color: C.muted, fontSize: 10, textTransform: "uppercase", letterSpacing: 1, margin: "4px 0 2px" }}>{label}</p>
      {sub && <p style={{ color: C.muted, fontSize: 10, margin: 0 }}>{sub}</p>}
    </div>
  );
  return (
    <div style={{ maxWidth: 620 }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 24, background: C.navyL, borderRadius: 14, padding: 6 }}>
        {[["today","Today"],["week","This Week"],["month","This Month"]].map(([k,l]) => (
          <button key={k} onClick={() => setPeriod(k)} style={{ flex:1, background: period===k ? C.gold : "transparent", color: period===k ? C.navy : C.muted, border:"none", borderRadius:10, padding:"9px 0", fontFamily:F.serif, fontWeight:700, fontSize:13, cursor:"pointer" }}>{l}</button>
        ))}
      </div>
      <p style={{ color:C.gold, fontFamily:F.serif, fontSize:12, textTransform:"uppercase", letterSpacing:1.5, margin:"0 0 12px", fontWeight:600 }}>👶 Children</p>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:20 }}>
        <StatCard icon="✅" label="Present" val={presentInPeriod.length} color={C.green} />
        <StatCard icon="❌" label="Absent" val={absentInPeriod.length} color={C.red} />
        <StatCard icon="🚗" label="Pickups" val={pickupsInPeriod.length} sub={`${pickupsInPeriod.filter(h=>h.photo).length} with photo`} color={C.gold} />
      </div>
      {sortedHours.length > 0 && (
        <div style={{ background:C.navyL, borderRadius:14, padding:"16px 18px", marginBottom:20, border:`1px solid ${C.gold}22` }}>
          <p style={{ color:C.gold, fontFamily:F.serif, fontSize:12, textTransform:"uppercase", letterSpacing:1, margin:"0 0 16px", fontWeight:600 }}>⏰ Pickup Times</p>
          <div style={{ display:"flex", gap:8, alignItems:"flex-end", height:80 }}>
            {sortedHours.map(([hour, count]) => (
              <div key={hour} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                <p style={{ color:C.cream, fontFamily:F.mono, fontSize:10, margin:0 }}>{count}</p>
                <div style={{ width:"100%", borderRadius:"4px 4px 0 0", height:`${Math.round((count/maxPickups)*60)+8}px`, background:`linear-gradient(180deg,${C.gold},${C.gold}88)` }} />
                <p style={{ color:C.muted, fontFamily:F.mono, fontSize:9, margin:0 }}>{hour}:00</p>
              </div>
            ))}
          </div>
          <p style={{ color:C.muted, fontFamily:F.serif, fontSize:11, margin:"12px 0 0" }}>Peak: {sortedHours.reduce((a,b)=>b[1]>a[1]?b:a)[0]}:00 — {sortedHours.reduce((a,b)=>b[1]>a[1]?b:a)[1]} pickups</p>
        </div>
      )}
      <p style={{ color:C.gold, fontFamily:F.serif, fontSize:12, textTransform:"uppercase", letterSpacing:1.5, margin:"0 0 12px", fontWeight:600 }}>👩‍🏫 Staff</p>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:20 }}>
        <StatCard icon="✅" label="Present" val={tPresent} color={C.green} />
        <StatCard icon="⚠️" label="Late" val={tLate} color={C.warn} />
        <StatCard icon="❌" label="Absent" val={tAbsent} color={C.red} />
        <StatCard icon="📅" label="On Leave" val={tLeave} color="#4fc3f7" />
      </div>
      <p style={{ color:C.gold, fontFamily:F.serif, fontSize:12, textTransform:"uppercase", letterSpacing:1.5, margin:"0 0 12px", fontWeight:600 }}>👤 Visitors & Appointments</p>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:12 }}>
        <StatCard icon="👤" label="Visitors" val={visitorsInPeriod.length} color={C.gold} />
        <StatCard icon="🚨" label="Flagged" val={flaggedVisitors.length} color={C.red} />
        <StatCard icon="📅" label="Appointments" val={apptsInPeriod.length} sub={`${apptsCompleted} completed`} color={C.green} />
      </div>
      {Object.keys(reasonCounts).length > 0 && (
        <div style={{ background:C.navyL, borderRadius:14, padding:"16px 18px", marginBottom:20, border:`1px solid ${C.gold}22` }}>
          <p style={{ color:C.gold, fontFamily:F.serif, fontSize:12, textTransform:"uppercase", letterSpacing:1, margin:"0 0 12px", fontWeight:600 }}>Reasons for Visit</p>
          {Object.entries(reasonCounts).sort((a,b)=>b[1]-a[1]).map(([reason,count]) => {
            const pct = Math.round((count/visitorsInPeriod.length)*100);
            return (
              <div key={reason} style={{ marginBottom:10 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                  <p style={{ color:C.cream, fontFamily:F.serif, fontSize:12, margin:0 }}>{reason}</p>
                  <p style={{ color:C.muted, fontFamily:F.mono, fontSize:11, margin:0 }}>{count} ({pct}%)</p>
                </div>
                <div style={{ height:5, background:"#ffffff14", borderRadius:5, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${pct}%`, background:`linear-gradient(90deg,${C.gold}88,${C.gold})`, borderRadius:5 }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
      {visitorsInPeriod.length===0 && teacherRecords.length===0 && pickupsInPeriod.length===0 && (
        <div style={{ background:C.navyL, borderRadius:14, padding:"28px", textAlign:"center", border:`1px solid ${C.gold}22` }}>
          <p style={{ color:C.muted, fontFamily:F.serif, fontSize:13, margin:0 }}>No data yet for this period. Stats will appear as the school uses the app. 📊</p>
        </div>
      )}
    </div>
  );
}// ── Admin Panel ───────────────────────────────────────────────────────────────
function AdminPanel({ children, onAdd, onDelete, onBack, settings, onSaveSettings, teachers, onAddTeacher, onDeleteTeacher }) {
  const [tab, setTab]     = useState("children");
  const [statPeriod, setStatPeriod] = useState("today");
  const [adding, setAdding] = useState(false);
  const [addingTeacher, setAddingTeacher] = useState(false);
  const [err, setErr]     = useState("");
  const [teacherErr, setTeacherErr] = useState("");
  const [manualPurged, setManualPurged] = useState(null);
  const [tForm, setTForm] = useState({ name:"", role:"", pin:"", emoji:"👩‍🏫", phone:"" });
  const [sForm, setSForm]   = useState({ ...settings });
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [ecForm, setEcForm] = useState({ name:"", relation:"", phone:"" });
  const [acForm, setAcForm] = useState({ name:"", relation:"", phone:"", pin:"" });

  const generateUniquePin = (existingPins) => {
    let pin;
    let attempts = 0;
    do {
      pin = String(Math.floor(1000 + Math.random() * 9000));
      attempts++;
    } while (existingPins.includes(pin) && attempts < 100);
    return pin;
  };

  const allChildPins = children.map(c => c.pin);
  const allTeacherPins = (teachers||[]).map(t => t.pin);

  const [form, setForm] = useState(() => ({
    name:"", guardian:"", guardianPhone:"",
    pin: generateUniquePin(allChildPins),
    photo: null, photoPreview: null,
    latePickupTime:"17:00"
  }));

  const setF = (k,v) => setForm(f => ({ ...f, [k]: v }));

  const resetForm = () => setForm({
    name:"", guardian:"", guardianPhone:"",
    pin: generateUniquePin(children.map(c=>c.pin)),
    photo: null, photoPreview: null,
    latePickupTime:"17:00"
  });

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const maxSize = 200;
        const ratio = Math.min(maxSize/img.width, maxSize/img.height);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        const compressed = canvas.toDataURL("image/jpeg", 0.7);
        setF("photo", compressed);
        setF("photoPreview", compressed);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleTeacherPhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const maxSize = 200;
        const ratio = Math.min(maxSize/img.width, maxSize/img.height);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        const compressed = canvas.toDataURL("image/jpeg", 0.7);
        setTForm(f => ({ ...f, photo: compressed }));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const submit = () => {
    if (!form.name || !form.guardian || form.pin.length !== 4) { setErr("Fill all required fields."); return; }
    if (children.find(c => c.pin === form.pin)) { setErr("That PIN is already in use. Please generate a new one."); return; }
    onAdd({
      ...form,
      photo: form.photo || "🧒",
      id: uid(), status: "at-school", pickupHistory: [],
      activityLog: { [dateKey()]: [] },
      emergencyContacts: [],
      authorisedCollectors: [],
      medicalInfo: {}
    });
    if (form.guardianPhone) {
      const msg = `Hello! 👋 Your child *${form.name}* has been enrolled. Your secure pickup PIN is: *${form.pin}*. Please save this PIN — you will need it every time you collect your child. Do not share it with anyone. — Nkanyezi SafePickup 🔒`;
      setTimeout(() => window.open(`https://wa.me/${normPhone(form.guardianPhone)}?text=${encodeURIComponent(msg)}`, "_blank"), 500);
    }
    setAdding(false);
    resetForm();
    setErr("");
  };

  const saveSettings = () => {
    onSaveSettings(sForm);
    setSettingsSaved(true);
    setTimeout(() => setSettingsSaved(false), 2500);
  };

  const PhotoDisplay = ({ photo, size=32 }) => {
    if (!photo) return <span style={{ fontSize:size*0.8 }}>🧒</span>;
    if (photo.startsWith("data:") || photo.startsWith("http")) {
      return <img src={photo} alt="profile" style={{ width:size, height:size, borderRadius:"50%", objectFit:"cover", border:`2px solid ${C.gold}44` }} />;
    }
    return <span style={{ fontSize:size*0.8 }}>{photo}</span>;
  };

  return (
    <div style={{ padding: "26px 28px", fontFamily: F.serif }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ color: C.gold, fontSize: 18, margin: 0 }}>⚙ Admin Panel</h2>
        <Btn variant="ghost" small onClick={onBack}>← Dashboard</Btn>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: `1px solid ${C.gold}22`, paddingBottom: 0, flexWrap:"wrap" }}>
       {[["children","Children"],["teachers","Teachers"],["settings","WhatsApp"],["storage","Storage"],["stats","📊 Stats"]].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            background: tab===k ? C.gold : "transparent", color: tab===k ? C.navy : C.muted,
            border: "none", borderRadius: "10px 10px 0 0", padding: "9px 16px",
            fontFamily: F.serif, fontSize: 13, fontWeight: 700, cursor: "pointer"
          }}>{l}</button>
        ))}
      </div>

      {tab === "children" && (
        <div>
          {!adding ? (
            <Btn onClick={() => { setAdding(true); resetForm(); }} style={{ marginBottom: 24 }}>+ Add New Child</Btn>
          ) : (
            <Card gold style={{ maxWidth: 560, marginBottom: 28 }}>
              <SectionTitle>New Child</SectionTitle>
              <div style={{ background:"#4fc3f722", border:"1px solid #4fc3f755", borderRadius:10, padding:"10px 14px", marginBottom:16 }}>
                <p style={{ color:"#4fc3f7", fontFamily:F.serif, fontSize:11, margin:0, lineHeight:1.6 }}>
                  📋 <strong>POPIA Notice:</strong> Child photos are personal information. Ensure you have written consent from the parent/guardian before uploading a photo.
                </p>
              </div>

              <div style={{ marginBottom:20, textAlign:"center" }}>
                <div style={{ width:80, height:80, borderRadius:"50%", background:C.navy, border:`3px solid ${C.gold}44`, margin:"0 auto 12px", overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  {form.photoPreview
                    ? <img src={form.photoPreview} alt="child" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                    : <span style={{ fontSize:36 }}>🧒</span>
                  }
                </div>
                <input type="file" accept="image/*" id="child-photo-upload" style={{ display:"none" }} onChange={handlePhotoUpload} />
                <label htmlFor="child-photo-upload" style={{
                  background:C.navyL, border:`1.5px solid ${C.gold}55`, borderRadius:10,
                  padding:"8px 18px", color:C.gold, fontFamily:F.serif, fontSize:12,
                  fontWeight:700, cursor:"pointer", display:"inline-block"
                }}>📷 Upload Photo (optional)</label>
                {form.photoPreview && (
                  <button onClick={()=>{setF("photo",null);setF("photoPreview",null);}} style={{
                    background:"transparent", border:"none", color:C.muted,
                    fontFamily:F.serif, fontSize:11, cursor:"pointer", marginLeft:8
                  }}>✕ Remove</button>
                )}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
                <Input label="Child's Full Name *" value={form.name} onChange={v => setF("name",v)} />
                <Input label="Guardian's Name *" value={form.guardian} onChange={v => setF("guardian",v)} />
                <Input label="Guardian Phone *" value={form.guardianPhone} onChange={v => setF("guardianPhone",v)} />
                <div style={{ marginBottom:16 }}>
                  <label style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:5 }}>
                    4-Digit PIN * <span style={{ color:C.muted, fontWeight:400, textTransform:"none", letterSpacing:0, fontSize:10 }}>(auto-generated, unique)</span>
                  </label>
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    <input value={form.pin} onChange={e=>setF("pin",e.target.value.replace(/\D/g,"").slice(0,4))}
                      style={{ flex:1, padding:"10px 14px", borderRadius:10, background:C.navy, border:`1px solid ${C.gold}55`, color:C.gold, fontFamily:F.mono, fontSize:20, outline:"none", textAlign:"center", letterSpacing:6 }}
                    />
                    <button onClick={()=>setF("pin", generateUniquePin(children.map(c=>c.pin)))} style={{
                      background:C.navyL, border:`1px solid ${C.gold}44`, borderRadius:10,
                      padding:"10px 12px", color:C.gold, fontFamily:F.serif, fontSize:11,
                      cursor:"pointer", whiteSpace:"nowrap"
                    }}>🔄 New PIN</button>
                  </div>
                  {children.find(c=>c.pin===form.pin) && <p style={{color:C.red,fontSize:11,margin:"4px 0 0"}}>⚠ PIN in use — tap 🔄 to generate a new one</p>}
                </div>
                <Input label="Late Pickup Time" value={form.latePickupTime} onChange={v => setF("latePickupTime",v)} type="time" />
              </div>

              <div style={{ background: C.gold+"18", border:`1px solid ${C.gold}44`, borderRadius:10, padding:"10px 14px", marginBottom:16 }}>
                <p style={{ color:C.gold, fontFamily:F.serif, fontSize:12, margin:0, lineHeight:1.6 }}>
                  💡 <strong>After saving</strong>, open the child's profile to add Emergency Contacts and Authorised Collectors via the dedicated tabs.
                </p>
              </div>

              {err && <p style={{ color:C.red, fontSize:13, marginBottom:12 }}>{err}</p>}
              <div style={{ display:"flex", gap:10 }}>
                <Btn onClick={submit}>Save Child</Btn>
                <Btn variant="ghost" onClick={()=>{setAdding(false);setErr("");}}>Cancel</Btn>
              </div>
            </Card>
          )}

          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"separate", borderSpacing:"0 8px" }}>
              <thead>
                <tr>{["","Name","Guardian","Late By","Collectors","Status",""].map((h,i) => (
                  <th key={i} style={{ color:C.muted, fontSize:10, textTransform:"uppercase", letterSpacing:1, textAlign:"left", paddingBottom:8, paddingLeft:12 }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {children.map(c => (
                  <tr key={c.id}>
                    <td style={{ background:C.navyL, borderRadius:"12px 0 0 12px", padding:"12px" }}><PhotoDisplay photo={c.photo} size={36} /></td>
                    <td style={{ background:C.navyL, color:C.cream, fontSize:13, padding:"12px", fontFamily:F.serif }}>{c.name}</td>
                    <td style={{ background:C.navyL, color:C.muted, fontSize:12, padding:"12px", fontFamily:F.serif }}>{c.guardian}</td>
                    <td style={{ background:C.navyL, color:C.gold, fontSize:12, padding:"12px", fontFamily:F.mono }}>{c.latePickupTime}</td>
                    <td style={{ background:C.navyL, padding:"12px" }}><Badge label={`${(c.authorisedCollectors||[]).length} collectors`} color={C.green} /></td>
                    <td style={{ background:C.navyL, padding:"12px" }}><Badge label={statusLabel(c.status)} color={statusColor(c.status)} /></td>
                    <td style={{ background:C.navyL, borderRadius:"0 12px 12px 0", padding:"12px" }}>
                      <Btn variant="danger" small onClick={() => onDelete(c.id)}>Remove</Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "teachers" && (
        <div>
          {!addingTeacher ? (
            <Btn onClick={() => {
              setAddingTeacher(true);
              setTForm({ name:"", role:"", pin: generateUniquePin(allTeacherPins), emoji:"👩‍🏫", photo:null, phone:"" });
            }} style={{ marginBottom:24 }}>+ Add Teacher</Btn>
          ) : (
            <Card gold style={{ maxWidth:460, marginBottom:24 }}>
              <SectionTitle>New Teacher</SectionTitle>
              <div style={{ marginBottom:20, textAlign:"center" }}>
                <div style={{ width:80, height:80, borderRadius:"50%", background:C.navy, border:`3px solid ${C.gold}44`, margin:"0 auto 12px", overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  {tForm.photo
                    ? <img src={tForm.photo} alt="teacher" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                    : <span style={{ fontSize:36 }}>{tForm.emoji}</span>
                  }
                </div>
                <input type="file" accept="image/*" id="teacher-photo-upload" style={{ display:"none" }} onChange={handleTeacherPhotoUpload} />
                <label htmlFor="teacher-photo-upload" style={{
                  background:C.navyL, border:`1.5px solid ${C.gold}55`, borderRadius:10,
                  padding:"8px 18px", color:C.gold, fontFamily:F.serif, fontSize:12,
                  fontWeight:700, cursor:"pointer", display:"inline-block"
                }}>📷 Upload Photo (optional)</label>
                {tForm.photo && (
                  <button onClick={()=>setTForm(f=>({...f,photo:null}))} style={{ background:"transparent", border:"none", color:C.muted, fontFamily:F.serif, fontSize:11, cursor:"pointer", marginLeft:8 }}>✕ Remove</button>
                )}
              </div>

              {!tForm.photo && (
                <div style={{ marginBottom:16 }}>
                  <label style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:1 }}>Or choose emoji</label>
                  <div style={{ display:"flex", gap:8, marginTop:8 }}>
                    {["👩‍🏫","👨‍🏫","👩‍💼","👨‍💼","🧑‍🏫"].map(e=>(
                      <button key={e} onClick={()=>setTForm(f=>({...f,emoji:e}))} style={{
                        fontSize:26, background:tForm.emoji===e?C.gold+"33":"transparent",
                        border:tForm.emoji===e?`2px solid ${C.gold}`:"2px solid transparent",
                        borderRadius:10, padding:6, cursor:"pointer"
                      }}>{e}</button>
                    ))}
                  </div>
                </div>
              )}

              <Input label="Full Name *" value={tForm.name} onChange={v=>setTForm(f=>({...f,name:v}))} />
              <Input label="Role / Class *" value={tForm.role} onChange={v=>setTForm(f=>({...f,role:v}))} placeholder="e.g. Grade R Teacher" />
              <Input label="Phone Number (WhatsApp) *" value={tForm.phone||""} onChange={v=>setTForm(f=>({...f,phone:v}))} placeholder="e.g. 082 111 2222" />

              <div style={{ marginBottom:16 }}>
                <label style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:5 }}>4-Digit PIN *</label>
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <input value={tForm.pin} onChange={e=>setTForm(f=>({...f,pin:e.target.value.replace(/\D/g,"").slice(0,4)}))}
                    style={{ flex:1, padding:"10px 14px", borderRadius:10, background:C.navy, border:`1px solid ${C.gold}55`, color:C.gold, fontFamily:F.mono, fontSize:20, outline:"none", textAlign:"center", letterSpacing:6 }}
                  />
                  <button onClick={()=>setTForm(f=>({...f,pin:generateUniquePin(allTeacherPins)}))} style={{
                    background:C.navyL, border:`1px solid ${C.gold}44`, borderRadius:10,
                    padding:"10px 12px", color:C.gold, fontFamily:F.serif, fontSize:11,
                    cursor:"pointer", whiteSpace:"nowrap"
                  }}>🔄 New PIN</button>
                </div>
                {(teachers||[]).find(t=>t.pin===tForm.pin) && <p style={{color:C.red,fontSize:11,margin:"4px 0 0"}}>⚠ PIN in use — tap 🔄 to generate a new one</p>}
              </div>

              {teacherErr && <p style={{ color:C.red, fontSize:13, marginBottom:12 }}>{teacherErr}</p>}
              <div style={{ display:"flex", gap:10 }}>
                <Btn onClick={() => {
                  if (!tForm.name||!tForm.role||tForm.pin.length!==4) { setTeacherErr("Fill all fields."); return; }
                  if ((teachers||[]).find(t=>t.pin===tForm.pin)) { setTeacherErr("That PIN is already in use."); return; }
                  onAddTeacher({ ...tForm, id:uid(), attendance:[] });
                  // Send WhatsApp PIN message to teacher
                  if (tForm.phone) {
                    const msg = `Hello ${tForm.name}! 👋 You have been added as a staff member at ${settings?.schoolName||"the school"} on Nkanyezi SafePickup. Your personal check-in PIN is: *${tForm.pin}*. Please save this PIN — you will need it every day to check in. Do not share it with anyone. — Nkanyezi SafePickup 🔒`;
                    setTimeout(() => window.open(`https://wa.me/${normPhone(tForm.phone)}?text=${encodeURIComponent(msg)}`, "_blank"), 500);
                  }
                  setTForm({ name:"", role:"", pin: generateUniquePin(allTeacherPins), emoji:"👩‍🏫", photo:null, phone:"" });
                  setAddingTeacher(false); setTeacherErr("");
                }}>Save Teacher</Btn>
                <Btn variant="ghost" onClick={()=>{setAddingTeacher(false);setTeacherErr("");}}>Cancel</Btn>
              </div>
            </Card>
          )}

          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {(teachers||[]).map(t => {
              const todayRec = t.attendance.find(a=>a.date===dateKey());
              return (
                <div key={t.id} style={{
                  background:C.navyL, borderRadius:14, padding:"14px 18px",
                  display:"flex", alignItems:"center", gap:14,
                  border:`1.5px solid ${todayRec ? (STATUS_COLORS[todayRec.status]||C.muted)+"33" : C.muted+"22"}`
                }}>
                  <div style={{ width:44, height:44, borderRadius:"50%", overflow:"hidden", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", background:C.navy, border:`2px solid ${C.gold}33` }}>
                    {t.photo && (t.photo.startsWith("data:")||t.photo.startsWith("http"))
                      ? <img src={t.photo} alt={t.name} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                      : <span style={{ fontSize:26 }}>{t.emoji||"👩‍🏫"}</span>
                    }
                  </div>
                  <div style={{ flex:1 }}>
                    <p style={{ color:C.cream, fontFamily:F.serif, fontWeight:700, fontSize:14, margin:0 }}>{t.name}</p>
                    <p style={{ color:C.muted, fontFamily:F.serif, fontSize:11, margin:"2px 0 0" }}>{t.role}</p>
                    {t.phone && <p style={{ color:C.muted, fontFamily:F.mono, fontSize:11, margin:"2px 0 0" }}>{t.phone}</p>}
                    <p style={{ color:C.muted, fontFamily:F.mono, fontSize:11, margin:"4px 0 0" }}>PIN: {"•".repeat(4)}</p>
                  </div>
                  <div style={{ textAlign:"right", display:"flex", flexDirection:"column", gap:8, alignItems:"flex-end" }}>
                    {todayRec
                      ? <span style={{ background:(STATUS_COLORS[todayRec.status]||C.muted)+"22", color:STATUS_COLORS[todayRec.status]||C.muted, borderRadius:20, padding:"3px 12px", fontSize:11, fontWeight:700, fontFamily:F.serif }}>{todayRec.status}</span>
                      : <span style={{ color:C.muted, fontSize:11, fontFamily:F.serif }}>Not checked in</span>
                    }
                    <Btn variant="danger" small onClick={()=>onDeleteTeacher(t.id)}>Remove</Btn>
                  </div>
                </div>
              );
            })}
            {(teachers||[]).length === 0 && (
              <p style={{ color:C.muted, fontFamily:F.serif, fontSize:13 }}>No teachers added yet.</p>
            )}
          </div>
        </div>
      )}

      {tab === "settings" && (
        <div style={{ maxWidth: 520 }}>
          <div style={{ background: C.navyL, borderRadius: 14, padding: "16px 20px", border: `1.5px solid ${C.green}44`, marginBottom: 24 }}>
            <p style={{ color: C.green, fontWeight: 700, fontFamily: F.serif, fontSize: 13, margin: "0 0 6px" }}>💬 How WhatsApp notifications work</p>
            <p style={{ color: C.muted, fontFamily: F.serif, fontSize: 12, margin: 0, lineHeight: 1.7 }}>
              When a pickup is confirmed, the app opens WhatsApp with a pre-written message. Staff tap Send — the school group and the guardian are notified instantly. No API or monthly cost needed.
            </p>
          </div>

          <Card gold style={{ marginBottom: 20 }}>
            <SectionTitle>School Details</SectionTitle>
            <Input label="School Name (appears in messages)" value={sForm.schoolName} onChange={v => setSForm(f=>({...f, schoolName:v}))} />
            <div style={{ marginBottom: 14 }}>
              <label style={{ color: C.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 5 }}>School Start Time</label>
              <input type="time" value={sForm.schoolStartTime||"08:00"}
                onChange={e => setSForm(f=>({...f, schoolStartTime:e.target.value}))}
                style={{ padding:"10px 14px", borderRadius:10, background:C.navy, border:`1px solid ${C.gold}33`, color:C.gold, fontFamily:F.mono, fontSize:16, outline:"none" }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ color: C.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 5 }}>WhatsApp Group Invite Link</label>
              <input value={sForm.schoolGroupLink} onChange={e => setSForm(f=>({...f, schoolGroupLink:e.target.value}))}
                placeholder="https://chat.whatsapp.com/XXXXXXXX"
                style={{ width:"100%", padding:"10px 14px", borderRadius:10, background:C.navy, border:`1px solid ${C.gold}33`, color:C.cream, fontFamily:F.serif, fontSize:13, outline:"none", boxSizing:"border-box" }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ color: C.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 5 }}>School Admin WhatsApp Number</label>
              <input value={sForm.schoolPhone} onChange={e => setSForm(f=>({...f, schoolPhone:e.target.value}))}
                placeholder="082 000 1111"
                style={{ width:"100%", padding:"10px 14px", borderRadius:10, background:C.navy, border:`1px solid ${C.gold}33`, color:C.cream, fontFamily:F.serif, fontSize:13, outline:"none", boxSizing:"border-box" }} />
            </div>
          </Card>

          <Card gold style={{ marginBottom: 20 }}>
            <SectionTitle>🔐 Principal PIN</SectionTitle>
            <div style={{ marginBottom:14 }}>
              <label style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:5 }}>New Principal PIN</label>
              <input value={sForm.adminPin || ""}
                onChange={e => setSForm(f=>({...f, adminPin: e.target.value.toUpperCase().replace(/\s/g,"")}))}
                placeholder="Enter new PIN (letters + numbers)"
                style={{ width:"100%", padding:"10px 14px", borderRadius:10, background:C.navy, border:`2px solid ${C.gold}55`, color:C.gold, fontFamily:F.mono, fontSize:16, outline:"none", boxSizing:"border-box", letterSpacing:2 }}
              />
              <p style={{ color:C.muted, fontSize:11, marginTop:5, fontFamily:F.serif }}>⚠ After saving, you will need this new PIN to log in as Principal next time.</p>
            </div>
          </Card>

          <Card style={{ marginBottom: 20, border: `1.5px solid ${C.red}44` }}>
            <SectionTitle>🚨 Panic Alert Contacts</SectionTitle>
            {[
              ["panicPrincipal",  "Principal's WhatsApp Number",         "e.g. 082 111 2222"],
              ["panicSecurity",   "Security Company WhatsApp Number",    "e.g. 011 000 1111"],
              ["panicEmergency",  "Third Emergency Contact",             "e.g. 083 222 3333"],
            ].map(([key, label, placeholder]) => (
              <div key={key} style={{ marginBottom:14 }}>
                <label style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:5 }}>{label}</label>
                <input value={sForm[key]||""} onChange={e => setSForm(f=>({...f,[key]:e.target.value}))}
                  placeholder={placeholder}
                  style={{ width:"100%", padding:"10px 14px", borderRadius:10, background:C.navy, border:`1px solid ${C.red}33`, color:C.cream, fontFamily:F.serif, fontSize:13, outline:"none", boxSizing:"border-box" }}
                />
              </div>
            ))}
          </Card>

          <Btn onClick={saveSettings} style={{ width: "100%", padding: "14px 0" }}>
            {settingsSaved ? "✅ Settings Saved!" : "💾 Save Settings"}
          </Btn>
        </div>
      )}

      {tab === "storage" && (() => {
        const mb    = parseFloat(estimateStorageMB(children));
        const stats = photoStats(children);
        const pct   = Math.min(100, Math.round((mb / 500) * 100));
        const barCol= pct > 80 ? C.red : pct > 50 ? C.warn : C.green;

        const runManualPurge = () => {
          const { cleaned, purgedCount } = purgeOldPhotos(children);
          window.dispatchEvent(new CustomEvent("nkanyezi-purge", { detail: { cleaned, purgedCount } }));
          setManualPurged(purgedCount);
        };

        return (
          <div style={{ maxWidth: 520 }}>
            <Card gold style={{ marginBottom: 20 }}>
              <SectionTitle>Current Photo Storage</SectionTitle>
              <div style={{ display: "flex", gap: 20, marginBottom: 16, flexWrap: "wrap" }}>
                {[
                  { label: "Total Events", val: stats.total, color: C.cream },
                  { label: "With Photo",   val: stats.withPhoto, color: C.gold },
                  { label: "Expiring Soon", val: stats.expiringSoon, color: C.warn },
                  { label: "Est. Size",    val: `${mb} MB`, color: C.green },
                ].map(s => (
                  <div key={s.label} style={{ textAlign: "center", minWidth: 80 }}>
                    <p style={{ color: s.color, fontFamily: F.serif, fontSize: 22, fontWeight: 700, margin: 0 }}>{s.val}</p>
                    <p style={{ color: C.muted, fontSize: 10, textTransform: "uppercase", letterSpacing: 1, margin: "4px 0 0" }}>{s.label}</p>
                  </div>
                ))}
              </div>
              <div style={{ height: 8, background: "#ffffff14", borderRadius: 8, overflow: "hidden", marginBottom: 8 }}>
                <div style={{ height: "100%", width: `${Math.max(pct,2)}%`, background: `linear-gradient(90deg, ${barCol}88, ${barCol})`, borderRadius: 8 }} />
              </div>
              <p style={{ color: C.muted, fontFamily: F.serif, fontSize: 11, margin: 0 }}>{mb} MB used · {pct}% full</p>
            </Card>

            {manualPurged !== null && (
              <div style={{ background: C.green+"22", border: `1px solid ${C.green}44`, borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}>
                <p style={{ color: C.green, fontFamily: F.serif, fontSize: 13, fontWeight: 700, margin: 0 }}>
                  ✅ {manualPurged === 0 ? "Nothing to clean up." : `${manualPurged} photo${manualPurged>1?"s":""} removed.`}
                </p>
              </div>
            )}
            <Btn variant="secondary" onClick={runManualPurge}>🗑 Run Clean-Up Now</Btn>
          </div>
        );
      })()}
       {tab === "stats" && (
          <StatsTab
            children={children}
            teachers={teachers}
            period={statPeriod}
            setPeriod={setStatPeriod}
          />
        )}
    </div>
  );
}

// ── APPOINTMENTS ──────────────────────────────────────────────────────────────
function AppointmentsScreen({ onBack, settings, staffName, teachers }) {
  const [appointments, setAppointments] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nkanyezi-appointments") || "[]"); } catch { return []; }
  });
  const [view, setView] = useState("today");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ with:"", purpose:"", visitorName:"", date: dateKey(), time:"08:00", createdBy: staffName||"Staff" });
  const setF = (k,v) => setForm(f=>({...f,[k]:v}));

  const save = (list) => {
    setAppointments(list);
    try { localStorage.setItem("nkanyezi-appointments", JSON.stringify(list)); } catch {}
  };

  const addAppointment = () => {
    if (!form.with || !form.purpose) return;
    const entry = { id: uid(), ...form, status:"expected", checkinTime:null, checkoutTime:null, createdAt: nowTime() };
    save([entry, ...appointments]);
    setAdding(false);
    setForm({ with:"", purpose:"", visitorName:"", date: dateKey(), time:"08:00", createdBy: staffName||"Staff" });
  };

  const updateStatus = (id, status) => {
    const now = nowTime();
    save(appointments.map(a => a.id === id ? {
      ...a, status,
      checkinTime: status === "arrived" ? now : a.checkinTime,
      checkoutTime: status === "complete" ? now : a.checkoutTime,
    } : a));
  };

  const filtered = appointments.filter(a => {
    if (view === "today") return a.date === dateKey();
    if (view === "upcoming") return a.date >= dateKey();
    return true;
  }).sort((a,b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`));

  const apptStatusColor = s => ({ expected:"#4fc3f7", arrived: C.gold, complete: C.green, cancelled: C.red, rescheduled: C.orange }[s] || C.muted);
  const apptStatusLabel = s => ({ expected:"📅 Expected", arrived:"🟡 Arrived", complete:"✅ Complete", cancelled:"❌ Cancelled", rescheduled:"🔄 Rescheduled" }[s] || s);

  return (
    <div style={{ minHeight:"100vh", padding:"24px", fontFamily:F.serif }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <div>
          <h2 style={{ color:C.gold, fontSize:20, margin:0 }}>📅 Appointments</h2>
          <p style={{ color:C.muted, fontSize:11, margin:"4px 0 0", letterSpacing:1 }}>{settings.schoolName}</p>
        </div>
        <Btn variant="ghost" small onClick={onBack}>← Back</Btn>
      </div>

      <Btn onClick={() => setAdding(true)} style={{ marginBottom:20, width:"100%" }}>+ New Appointment</Btn>

      {adding && (
        <Card gold style={{ marginBottom:20 }}>
          <SectionTitle>New Appointment</SectionTitle>
          <div style={{ marginBottom:14 }}>
            <label style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:5 }}>Appointment With *</label>
            <select value={form.with} onChange={e=>setF("with",e.target.value)}
              style={{ width:"100%", padding:"10px 14px", borderRadius:10, background:C.navy, border:`1px solid ${C.gold}33`, color:C.cream, fontFamily:F.serif, fontSize:14, outline:"none", boxSizing:"border-box" }}>
              <option value="">— Select staff member —</option>
              <option value="Principal">👑 Principal</option>
              {(teachers||[]).map(t => (
                <option key={t.id} value={t.name}>{t.emoji} {t.name} — {t.role}</option>
              ))}
            </select>
          </div>
          <Input label="Purpose *" value={form.purpose} onChange={v=>setF("purpose",v)} placeholder="e.g. Parent consultation" />
          <Input label="Visitor / Parent Name" value={form.visitorName} onChange={v=>setF("visitorName",v)} placeholder="e.g. Mrs Dlamini" />
          <Input label="Created By" value={form.createdBy} onChange={v=>setF("createdBy",v)} placeholder="e.g. Ms Naidoo" />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
            <div>
              <label style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:5 }}>Date</label>
              <input type="date" value={form.date} onChange={e=>setF("date",e.target.value)}
                style={{ width:"100%", padding:"10px 14px", borderRadius:10, background:C.navy, border:`1px solid ${C.gold}33`, color:C.gold, fontFamily:F.mono, fontSize:14, outline:"none", boxSizing:"border-box" }} />
            </div>
            <div>
              <label style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:5 }}>Time</label>
              <input type="time" value={form.time} onChange={e=>setF("time",e.target.value)}
                style={{ width:"100%", padding:"10px 14px", borderRadius:10, background:C.navy, border:`1px solid ${C.gold}33`, color:C.gold, fontFamily:F.mono, fontSize:14, outline:"none", boxSizing:"border-box" }} />
            </div>
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <Btn onClick={addAppointment}>Save Appointment</Btn>
            <Btn variant="ghost" onClick={() => setAdding(false)}>Cancel</Btn>
          </div>
        </Card>
      )}

      <div style={{ display:"flex", gap:4, marginBottom:16, borderBottom:`1px solid ${C.gold}22` }}>
        {[["today","Today"],["upcoming","Upcoming"],["all","All"]].map(([k,l]) => (
          <button key={k} onClick={() => setView(k)} style={{
            background:"transparent", color: view===k ? C.gold : C.muted,
            border:"none", borderBottom: view===k ? `3px solid ${C.gold}` : "3px solid transparent",
            padding:"10px 16px", fontFamily:F.serif, fontSize:13, fontWeight:700, cursor:"pointer"
          }}>{l}</button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign:"center", padding:"40px 0" }}>
          <p style={{ color:C.muted, fontFamily:F.serif, fontSize:13 }}>No appointments for this period.</p>
        </div>
      )}

      {filtered.map(a => (
        <div key={a.id} style={{ background:C.navyL, borderRadius:16, padding:"16px 18px", marginBottom:12, border:`1.5px solid ${apptStatusColor(a.status)}33` }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
            <div>
              <p style={{ color:C.cream, fontWeight:700, fontSize:15, margin:0 }}>{a.with}</p>
              <p style={{ color:C.muted, fontSize:12, margin:"3px 0 0" }}>{a.purpose}</p>
              {a.visitorName && <p style={{ color:C.cream, fontFamily:F.serif, fontWeight:700, fontSize:12, margin:"3px 0 0" }}>👤 {a.visitorName}</p>}
              <p style={{ color:C.muted, fontSize:11, margin:"3px 0 0" }}>
                {new Date(a.date+"T00:00:00").toLocaleDateString("en-ZA",{weekday:"short",day:"numeric",month:"short"})} · {a.time}
              </p>
              {a.createdBy && <p style={{ color:C.muted, fontSize:10, margin:"3px 0 0" }}>Added by: {a.createdBy}</p>}
            </div>
            <Badge label={apptStatusLabel(a.status)} color={apptStatusColor(a.status)} />
          </div>

          {(a.checkinTime || a.checkoutTime) && (
            <div style={{ display:"flex", gap:16, marginBottom:12 }}>
              {a.checkinTime && <p style={{ color:C.green, fontFamily:F.mono, fontSize:11, margin:0 }}>In: {a.checkinTime}</p>}
              {a.checkoutTime && <p style={{ color:C.gold, fontFamily:F.mono, fontSize:11, margin:0 }}>Out: {a.checkoutTime}</p>}
            </div>
          )}

          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {a.status === "expected" && <>
              <Btn small variant="green" onClick={() => updateStatus(a.id, "arrived")}>✅ Mark Arrived</Btn>
              <Btn small variant="ghost" onClick={() => updateStatus(a.id, "cancelled")}>❌ Cancel</Btn>
              <Btn small variant="ghost" onClick={() => updateStatus(a.id, "rescheduled")}>🔄 Reschedule</Btn>
            </>}
            {a.status === "arrived" && <>
              <Btn small variant="green" onClick={() => updateStatus(a.id, "complete")}>✅ Mark Complete</Btn>
            </>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── VISITOR LOG ───────────────────────────────────────────────────────────────
function VisitorLog({ onBack, schoolName, schoolWhatsApp, onToast }) {
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef   = useRef(null);

  const [step, setStep]       = useState("form");
  const [form, setForm]       = useState({ name:"", phone:"", vehicle:"", reason:"viewing" });
  const [captured, setCaptured] = useState(null);
  const [visitors, setVisitors] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nkanyezi-visitors") || "[]"); } catch { return []; }
  });
  const [flagging, setFlagging] = useState(null);
  const setF = (k,v) => setForm(f=>({...f,[k]:v}));

  const applyWatermark = (src) => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const barH = Math.max(52, c.height * 0.08);
    ctx.fillStyle = "rgba(6,14,31,0.85)";
    ctx.fillRect(0, c.height-barH, c.width, barH);
    ctx.fillStyle = "#E74C3C"; ctx.font = `bold ${Math.max(14,c.width/36)}px serif`;
    ctx.fillText("⭐ Nkanyezi SafePickup — VISITOR LOG", 14, c.height-barH+Math.max(14,c.width/36)+4);
    ctx.fillStyle = "#FDF6E3cc"; ctx.font = `${Math.max(11,c.width/52)}px serif`;
    ctx.fillText(`${form.name||"Unknown"} · ${form.phone||"No phone"} · ${nowTime()} ${dateKey()} · ${schoolName}`, 14, c.height-barH+Math.max(14,c.width/36)*2+10);
    return c.toDataURL("image/jpeg", 0.88);
  };

  const handleUpload = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const img = new Image();
      img.onload = () => {
        const c = canvasRef.current;
        c.width = img.naturalWidth; c.height = img.naturalHeight;
        c.getContext("2d").drawImage(img,0,0);
        setCaptured(applyWatermark(c)); setStep("preview");
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const saveVisitor = (flagged=false) => {
    const entry = {
      id: uid(), date: dateKey(), time: nowTime(),
      name: form.name||"Unknown", phone: form.phone, vehicle: form.vehicle,
      reason: form.reason, photo: captured, flagged,
      school: schoolName
    };
    const updated = [entry, ...visitors];
    setVisitors(updated);
    try { localStorage.setItem("nkanyezi-visitors", JSON.stringify(updated.slice(0,200))); } catch {}

    const schoolMsg =
      `${flagged ? "🚨 SUSPICIOUS VISITOR ALERT" : "👤 VISITOR LOG"} — ${schoolName}\n` +
      `──────────────────\n` +
      `Name: *${entry.name}*\n` +
      `Phone: ${entry.phone||"Not given"}\n` +
      `Vehicle: ${entry.vehicle||"Not noted"}\n` +
      `Reason: ${entry.reason}\n` +
      `Time: *${entry.time}* · ${entry.date}\n` +
      `──────────────────\n` +
      (flagged ? `⚠ This visitor has been flagged as SUSPICIOUS.\n` : "") +
      `_Nkanyezi SafePickup Visitor Log_`;

    if (schoolWhatsApp && flagged) {
      setTimeout(() => window.open(`https://wa.me/${normPhone(schoolWhatsApp)}?text=${encodeURIComponent(schoolMsg)}`, "_blank"), 400);
    }

    setFlagging(flagged);
    setStep("done");
    if (flagged) onToast("🚨 Suspicious visitor flagged — alert sent to school WhatsApp.", "warn");
    else onToast("✅ Visitor logged.", "info");

    setForm({ name:"", phone:"", vehicle:"", reason:"viewing" });
    setCaptured(null);
  };

  const reasonOptions = ["Viewing the school", "Delivery", "Maintenance / contractor", "Lost / asking directions", "Other"];

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ width:"100%", maxWidth:480, fontFamily:F.serif }}>
        <div style={{ textAlign:"center", marginBottom:24 }}>
          <span style={{ fontSize:28 }}>⭐</span>
          <h2 style={{ color:C.red, fontSize:18, margin:"6px 0 4px" }}>Visitor Verification</h2>
          <p style={{ color:C.muted, fontSize:11, letterSpacing:1, textTransform:"uppercase" }}>Nkanyezi SafePickup — {schoolName}</p>
        </div>

        {step === "log" && (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <h3 style={{ color:C.cream, fontFamily:F.serif, fontSize:16, margin:0 }}>Visitor History</h3>
              <Btn small variant="ghost" onClick={() => setStep("form")}>← Back</Btn>
            </div>
            {visitors.length === 0
              ? <p style={{ color:C.muted, fontFamily:F.serif, fontSize:13 }}>No visitors logged yet.</p>
              : visitors.map(v => (
                <div key={v.id} style={{
                  background: v.flagged ? C.red+"18" : C.navyL, borderRadius:14, padding:"14px 16px",
                  border:`1.5px solid ${v.flagged ? C.red : C.gold}33`, marginBottom:10,
                  display:"grid", gridTemplateColumns: v.photo ? "70px 1fr" : "1fr", gap:12, alignItems:"center"
                }}>
                  {v.photo && <img src={v.photo} alt="Visitor" style={{ width:66, height:50, objectFit:"cover", borderRadius:8, border:`2px solid ${v.flagged?C.red:C.gold}44` }} />}
                  <div>
                    <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:4 }}>
                      <p style={{ color:C.cream, fontWeight:700, fontSize:14, margin:0 }}>{v.name}</p>
                      {v.flagged && <Badge label="🚨 FLAGGED" color={C.red} />}
                    </div>
                    <p style={{ color:C.muted, fontSize:11, margin:0 }}>{v.reason} · {v.phone||"No phone"} · {v.vehicle||"No vehicle"}</p>
                    <p style={{ color:C.gold, fontFamily:F.mono, fontSize:11, margin:"3px 0 0" }}>{v.time} · {v.date}</p>
                    {!v.checkedOut && (
                      <div style={{ marginTop:8 }}>
                        <Btn small variant="secondary" onClick={() => {
                          const updated = visitors.map(vis => vis.id === v.id ? { ...vis, checkedOut:true, checkoutTime:nowTime() } : vis);
                          setVisitors(updated);
                          try { localStorage.setItem("nkanyezi-visitors", JSON.stringify(updated)); } catch {}
                        }}>🚪 Visitor has left</Btn>
                      </div>
                    )}
                    {v.checkedOut && (
                      <p style={{ color:C.green, fontFamily:F.mono, fontSize:11, margin:"8px 0 0" }}>✅ Departed: {v.checkoutTime}</p>
                    )}
                  </div>
                </div>
              ))
            }
          </div>
        )}

        {step === "form" && (
          <div>
            <Card gold style={{ marginBottom:16 }}>
              <SectionTitle>Visitor Details</SectionTitle>
              <p style={{ color:C.muted, fontFamily:F.serif, fontSize:12, marginBottom:16, lineHeight:1.7 }}>
                Ask the visitor for their details. All information and their photo will be distributed to the school immediately.
              </p>
              <Input label="Full Name" value={form.name} onChange={v=>setF("name",v)} placeholder="e.g. John Smith" />
              <Input label="Phone Number" value={form.phone} onChange={v=>setF("phone",v)} placeholder="e.g. 082 000 1111" />
              <Input label="Vehicle Description" value={form.vehicle} onChange={v=>setF("vehicle",v)} placeholder="e.g. Red Renault, CA 123 GP" />
              <div style={{ marginBottom:14 }}>
                <label style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:6 }}>Reason for Visit</label>
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {reasonOptions.map(r => (
                    <button key={r} onClick={()=>setF("reason",r)} style={{
                      background: form.reason===r ? C.gold+"33" : C.navy,
                      border: `1.5px solid ${form.reason===r ? C.gold : C.gold+"22"}`,
                      borderRadius:10, padding:"9px 14px", color: form.reason===r ? C.gold : C.muted,
                      fontFamily:F.serif, fontSize:13, cursor:"pointer", textAlign:"left",
                      fontWeight: form.reason===r ? 700 : 400
                    }}>{r}</button>
                  ))}
                </div>
              </div>
            </Card>
            <Btn onClick={()=>setStep("camera")} style={{ width:"100%", padding:"15px 0", fontSize:15, marginBottom:12 }}>📸 Capture Visitor Photo →</Btn>
            <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
              <Btn variant="ghost" small onClick={() => setStep("log")}>📋 View Log</Btn>
              <Btn variant="ghost" small onClick={onBack}>← Home</Btn>
            </div>
          </div>
        )}

        {step === "camera" && (
          <div>
            <Card style={{ marginBottom:14, border:`1.5px solid ${C.red}44` }}>
              <p style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:1, margin:"0 0 4px" }}>Capturing visitor</p>
              <p style={{ color:C.cream, fontWeight:700, fontSize:15, margin:0 }}>{form.name||"Visitor"}</p>
            </Card>
            <div style={{ background:C.navyL, borderRadius:16, padding:"28px 20px", border:`1.5px solid ${C.red}44`, textAlign:"center" }}>
              <div style={{ fontSize:48, marginBottom:14 }}>📷</div>
              <p style={{ color:C.cream, fontFamily:F.serif, fontWeight:700, fontSize:15, margin:"0 0 8px" }}>Photo Required</p>
              <p style={{ color:C.muted, fontFamily:F.serif, fontSize:12, margin:"0 0 20px", lineHeight:1.6 }}>
                A photo is required for all walk-in visitors. If visitor refuses, they cannot enter.
                Visitors who prefer not to be photographed may <strong style={{color:C.gold}}>book an appointment</strong> in advance.
              </p>
              <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={handleUpload} style={{ display:"none" }} />
              <button onClick={()=>fileRef.current?.click()} style={{
                width:"100%", background:C.red, color:"#fff",
                border:"none", borderRadius:14, padding:"18px 0", fontSize:16,
                fontFamily:F.serif, fontWeight:700, cursor:"pointer",
                boxShadow:`0 6px 20px ${C.red}44`, marginBottom:12
              }}>📸 Take / Upload Photo</button>
              <Btn variant="ghost" onClick={()=>setStep("form")}>← Back to Visitor Form</Btn>
            </div>
          </div>
        )}

        {step === "preview" && captured && (
          <div style={{ textAlign:"center" }}>
            <div style={{ position:"relative", borderRadius:16, overflow:"hidden", marginBottom:14, border:`2px solid ${C.red}55` }}>
              <img src={captured} alt="Visitor" style={{ width:"100%", display:"block", borderRadius:14 }} />
              <div style={{ position:"absolute", top:10, right:10, background:C.red, borderRadius:20, padding:"4px 12px", color:"#fff", fontSize:11, fontWeight:700, fontFamily:F.serif }}>VISITOR CAPTURED</div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <Btn variant="green" onClick={()=>setStep("popia-visitor")} style={{ padding:"14px 0", fontSize:14 }}>✅ Log as Normal Visitor</Btn>
              <button onClick={()=>saveVisitor(true)} style={{
                background:C.red+"22", color:C.red, border:`2px solid ${C.red}`,
                borderRadius:14, padding:"14px 0", fontSize:14, fontFamily:F.serif,
                fontWeight:700, cursor:"pointer"
              }}>🚨 Visitor does not require entry</button>
              <Btn variant="ghost" onClick={()=>setStep("camera")}>🔄 Retake Photo</Btn>
            </div>
          </div>
        )}

        {step === "popia-visitor" && (
          <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
            <div style={{ maxWidth:340, width:"100%", textAlign:"center", fontFamily:F.serif }}>
              <span style={{ fontSize:40 }}>🔒</span>
              <h2 style={{ color:C.gold, fontSize:20, margin:"12px 0 6px" }}>Privacy Notice</h2>
              <p style={{ color:C.muted, fontSize:11, letterSpacing:1, marginBottom:20 }}>POPIA — Protection of Personal Information Act</p>
              <div style={{ background:C.navyL, borderRadius:16, padding:"20px", marginBottom:20, textAlign:"left" }}>
                <p style={{ color:C.cream, fontSize:13, marginBottom:12 }}>By proceeding, you consent to Nkanyezi SafePickup collecting:</p>
                <p style={{ color:C.muted, fontSize:12, marginBottom:8 }}>📸 A photo for identity verification</p>
                <p style={{ color:C.muted, fontSize:12, marginBottom:8 }}>👤 Your name and reason for visit</p>
                <p style={{ color:C.muted, fontSize:12, marginBottom:16 }}>🕐 Date and time of your visit</p>
                <p style={{ color:C.muted, fontSize:11 }}>Data is retained for 90 days. Photos and records may be shared with members of the SafePickup School Safety Network and/or with law enforcement authorities to assist with investigations where necessary..</p>
              </div>
              <Btn variant="green" onClick={() => saveVisitor(false)} style={{ width:"100%", marginBottom:12 }}>✅ I Consent — Proceed</Btn>
              <Btn variant="ghost" onClick={() => setStep("preview")} style={{ width:"100%" }}>❌ I Do Not Consent — Cancel</Btn>
            </div>
          </div>
        )}

        {step === "done" && (
          <div style={{ textAlign:"center" }}>
            <div style={{ fontSize:64, marginBottom:12 }}>{flagging ? "🚨" : "✅"}</div>
            <h3 style={{ color: flagging ? C.red : C.gold, fontFamily:F.serif, fontSize:20, margin:"0 0 10px" }}>
              {flagging ? "Suspicious Visitor Flagged!" : "Visitor Logged!"}
            </h3>
            <div style={{ background:C.navyL, borderRadius:14, padding:"16px 18px", border:`1.5px solid ${C.gold}44`, marginBottom:20, textAlign:"left" }}>
              <p style={{ color:C.gold, fontFamily:F.serif, fontWeight:700, fontSize:13, margin:"0 0 8px" }}>💬 What to say to the visitor now:</p>
              <p style={{ color:C.cream, fontFamily:F.serif, fontSize:13, lineHeight:1.8, margin:0, fontStyle:"italic" }}>
                {flagging
                  ? `"Thank you for your patience. We are unable to accommodate walk-in viewings. Please call us to make an appointment."`
                  : `"Thank you — we've registered your visit. Someone will be with you shortly."`
                }
              </p>
            </div>
            <div style={{ display:"flex", gap:12, justifyContent:"center" }}>
              <Btn onClick={()=>setStep("form")}>Log Another Visitor</Btn>
              <Btn variant="ghost" onClick={()=>setStep("log")}>📋 View Log</Btn>
              <Btn variant="ghost" onClick={onBack}>← Home</Btn>
            </div>
          </div>
        )}

        <canvas ref={canvasRef} style={{ display:"none" }} />
      </div>
    </div>
  );
}

// ── TEACHER LOGIN ─────────────────────────────────────────────────────────────
function TeacherLogin({ teachers, onSuccess, onBack }) {
  const [error, setError] = useState("");

  const handlePin = (p) => {
    const found = teachers.find(t => t.pin === p);
    if (found) { setError(""); onSuccess(found); }
    else { setError("Incorrect PIN. Please try again."); }
  };

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ width:"100%", maxWidth:380, textAlign:"center", fontFamily:F.serif }}>
        <span style={{ fontSize:48, display:"block", marginBottom:12 }}>👩‍🏫</span>
        <h2 style={{ color:C.gold, fontSize:20, margin:"0 0 6px" }}>Teacher Check-In</h2>
        <p style={{ color:C.muted, fontSize:12, marginBottom:32, letterSpacing:1, textTransform:"uppercase" }}>Enter your personal PIN</p>
        <PinPad label="Your 4-digit PIN" onSubmit={handlePin} onBack={onBack} />
        {error && <p style={{ color:C.red, fontFamily:F.serif, fontSize:13, marginTop:14 }}>{error}</p>}
      </div>
    </div>
  );
}

// ── REQUEST LEAVE CARD ────────────────────────────────────────────────────────
function RequestLeaveCard({ teacher, onUpdateTeachers, today }) {
  const [open, setOpen]     = useState(false);
  const [date, setDate]     = useState(() => {
    const d = new Date(); d.setDate(d.getDate()+1);
    return d.toISOString().slice(0,10);
  });
  const [reason, setReason] = useState("On Leave");
  const [saved, setSaved]   = useState(false);

  const upcoming = teacher.attendance
    .filter(a => a.date > today && a.loggedBy === "teacher")
    .sort((a,b) => a.date.localeCompare(b.date));

  const handleSave = () => {
    if (teacher.attendance.find(a => a.date === date)) {
      alert("There is already an attendance record for that date.");
      return;
    }
    const entry = { date, arrivalTime: null, departureTime: null, status: reason, loggedBy: "teacher" };
    const updated = { ...teacher, attendance: [...teacher.attendance, entry] };
    onUpdateTeachers(updated);
    setSaved(true);
    setOpen(false);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div style={{ marginBottom:20 }}>
      {upcoming.length > 0 && (
        <div style={{ background:"#4fc3f722", border:"1px solid #4fc3f755", borderRadius:12, padding:"12px 16px", marginBottom:12 }}>
          <p style={{ color:"#4fc3f7", fontFamily:F.serif, fontWeight:700, fontSize:13, margin:"0 0 8px" }}>📅 Your Planned Leave</p>
          {upcoming.map((r,i) => (
            <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"4px 0" }}>
              <p style={{ color:C.cream, fontFamily:F.serif, fontSize:12, margin:0 }}>
                {new Date(r.date+"T00:00:00").toLocaleDateString("en-ZA",{weekday:"long",day:"numeric",month:"long"})}
              </p>
              <span style={{ background:(STATUS_COLORS[r.status]||C.muted)+"22", color:STATUS_COLORS[r.status]||C.muted, borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700 }}>{r.status}</span>
            </div>
          ))}
        </div>
      )}

      {saved && (
        <div style={{ background:C.green+"22", border:`1px solid ${C.green}44`, borderRadius:12, padding:"10px 14px", marginBottom:12 }}>
          <p style={{ color:C.green, fontFamily:F.serif, fontWeight:700, fontSize:13, margin:0 }}>✅ Leave request logged!</p>
        </div>
      )}

      {!open ? (
        <Btn variant="secondary" onClick={()=>setOpen(true)} style={{ width:"100%", marginBottom:4 }}>📅 Request Leave for a Future Date</Btn>
      ) : (
        <div style={{ background:C.navyL, borderRadius:16, padding:"20px 18px", border:`1.5px solid ${C.gold}33` }}>
          <p style={{ color:C.gold, fontFamily:F.serif, fontWeight:700, fontSize:14, margin:"0 0 16px" }}>Request Leave</p>
          <div style={{ marginBottom:14 }}>
            <label style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:6 }}>Date of Absence</label>
            <input type="date" value={date}
              min={(() => { const d=new Date(); d.setDate(d.getDate()+1); return d.toISOString().slice(0,10); })()}
              onChange={e=>setDate(e.target.value)}
              style={{ width:"100%", padding:"10px 14px", borderRadius:10, background:C.navy, border:`1px solid ${C.gold}33`, color:C.gold, fontFamily:F.mono, fontSize:14, outline:"none", boxSizing:"border-box" }}
            />
          </div>
          <div style={{ marginBottom:16 }}>
            <label style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:6 }}>Reason</label>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {["On Leave","Sick","Half Day","Personal"].map(s=>(
                <button key={s} onClick={()=>setReason(s)} style={{
                  background:reason===s?(STATUS_COLORS[s]||C.gold)+"44":(STATUS_COLORS[s]||C.gold)+"11",
                  color:STATUS_COLORS[s]||C.gold,
                  border:`1.5px solid ${reason===s?(STATUS_COLORS[s]||C.gold):"transparent"}`,
                  borderRadius:10, padding:"10px 0",
                  fontFamily:F.serif, fontWeight:700, fontSize:13, cursor:"pointer"
                }}>{s}</button>
              ))}
            </div>
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <Btn onClick={handleSave} style={{ flex:1 }}>Submit Request</Btn>
            <Btn variant="ghost" onClick={()=>setOpen(false)}>Cancel</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

// ── TEACHER DASHBOARD ─────────────────────────────────────────────────────────
function TeacherDashboard({ teacher, teachers, children, onLogout, onUpdateTeachers, onUpdateChild, settings }) {
  const today       = dateKey();
  const todayRecord = teacher.attendance.find(a => a.date === today);
  const isCheckedIn = todayRecord && todayRecord.arrivalTime && !todayRecord.departureTime;
  const isDone      = todayRecord && todayRecord.departureTime;

  const [view, setView]         = useState("overview");
  const [statusPick, setStatusPick] = useState(false);

  const SBadge = ({ status }) => (
    <span style={{
      background:(STATUS_COLORS[status]||C.muted)+"22", color:STATUS_COLORS[status]||C.muted,
      borderRadius:20, padding:"3px 12px", fontSize:11, fontWeight:700, fontFamily:F.serif,
      border:`1px solid ${(STATUS_COLORS[status]||C.muted)}44`, whiteSpace:"nowrap"
    }}>{status}</span>
  );

  const sendAttendanceWA = (type, time, status) => {
    const emoji = type === "in" ? "✅" : "🚪";
    const msg =
      `${emoji} *STAFF ${type === "in" ? "CHECK-IN" : "CHECK-OUT"}* — ${settings.schoolName}\n` +
      `──────────────────\n` +
      `Teacher: *${teacher.name}*\n` +
      `Role: ${teacher.role}\n` +
      `Status: *${status}*\n` +
      `Time: *${time}* · ${today}\n` +
      `──────────────────\n` +
      (type === "in" && status === "Late" ? `⚠ This teacher has checked in LATE.\n` : "") +
      `_Nkanyezi SafePickup — Staff Attendance_`;

    const target = settings.schoolGroupLink || settings.schoolPhone || settings.panicPrincipal;
    if (target) {
      setTimeout(() => window.open(waLink(target.startsWith("http") ? settings.schoolPhone || "" : target, msg), "_blank"), 200);
    }
  };

  const checkIn = (status) => {
    const isLateCheck = (() => {
      const [sh, sm] = (settings.schoolStartTime || "08:00").split(":").map(Number);
      const n = new Date();
      return n.getHours() > sh || (n.getHours() === sh && n.getMinutes() > sm);
    })();
    const resolved = status || (isLateCheck ? "Late" : "Present");
    const time = nowTime();
    const entry = { date: today, arrivalTime: time, departureTime: null, status: resolved };
    const updated = { ...teacher, attendance: [...teacher.attendance.filter(a => a.date !== today), entry] };
    onUpdateTeachers(updated);
    setStatusPick(false);
    sendAttendanceWA("in", time, resolved);
  };

  const checkOut = () => {
    const time = nowTime();
    const updated = { ...teacher, attendance: teacher.attendance.map(a => a.date === today ? { ...a, departureTime: time } : a) };
    onUpdateTeachers(updated);
    sendAttendanceWA("out", time, todayRecord?.status || "Present");
  };

  const inSchool   = children.filter(c => c.status !== "picked-up");
  const pickedUp   = children.filter(c => c.status === "picked-up");
  const lateKids   = children.filter(c => isLate(c));

  const tabs = [
    ["overview", "Overview"],
    ["attendance", "Attendance"],
    ["children", "Children"],
    ["staff",    "Staff"],
    ["my",       "My Attendance"],
    ["history",  "History"],
  ];

  const exportCSV = () => {
    const rows = ["Date,Arrival,Departure,Status"];
    [...teacher.attendance].sort((a,b)=>b.date.localeCompare(a.date)).forEach(r => {
      rows.push(`${r.date},${r.arrivalTime||""},${r.departureTime||""},${r.status}`);
    });
    const blob = new Blob([rows.join("\n")],{type:"text/csv"});
    const a = document.createElement("a"); a.href=URL.createObjectURL(blob);
    a.download=`${teacher.name.replace(/ /g,"_")}_attendance.csv`; a.click();
  };

  return (
    <div style={{ minHeight:"100vh", fontFamily:F.serif, background:`radial-gradient(ellipse at 10% 10%, #1a2a5e 0%, ${C.navy} 60%, ${C.navyD} 100%)` }}>

      <div style={{ background:C.navyL, padding:"18px 24px", borderBottom:`2px solid ${C.gold}33`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ fontSize:32 }}>{teacher.emoji}</span>
          <div>
            <p style={{ color:C.cream, fontFamily:F.serif, fontWeight:700, fontSize:15, margin:0 }}>{teacher.name}</p>
            <div style={{ display:"flex", gap:8, alignItems:"center", marginTop:3 }}>
              <p style={{ color:C.muted, fontFamily:F.serif, fontSize:11, margin:0 }}>{teacher.role}</p>
              {todayRecord && <SBadge status={todayRecord.status} />}
              {todayRecord && <span style={{ color:C.gold, fontFamily:F.mono, fontSize:11 }}>In: {todayRecord.arrivalTime}{todayRecord.departureTime ? ` · Out: ${todayRecord.departureTime}` : ""}</span>}
            </div>
          </div>
        </div>
        <Btn variant="ghost" small onClick={onLogout}>Sign Out</Btn>
      </div>

      <div style={{ background:C.navyL, padding:"0 24px", display:"flex", gap:4, overflowX:"auto", borderBottom:`1px solid ${C.gold}22` }}>
        {tabs.map(([k,l]) => (
          <button key={k} onClick={()=>setView(k)} style={{
            background:"transparent", color: view===k ? C.gold : C.muted,
            border:"none", borderBottom: view===k ? `3px solid ${C.gold}` : "3px solid transparent",
            padding:"12px 16px", fontFamily:F.serif, fontSize:12, fontWeight:700,
            cursor:"pointer", whiteSpace:"nowrap"
          }}>{l}</button>
        ))}
      </div>

      <div style={{ padding:"22px 24px" }}>

        {view === "overview" && (
          <div>
            <Card gold style={{ marginBottom:20, textAlign:"center" }}>
              <p style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:1, margin:"0 0 4px" }}>
                {new Date().toLocaleDateString("en-ZA",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}
              </p>

              {!todayRecord && !statusPick && (
                <div style={{ paddingTop:8 }}>
                  <p style={{ color:C.cream, fontSize:14, marginBottom:18 }}>Good day! You have not checked in yet.</p>
                  <div style={{ display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap" }}>
                    <Btn variant="green" onClick={()=>checkIn(null)} style={{ padding:"13px 28px" }}>Check In Now</Btn>
                    <Btn variant="secondary" onClick={()=>setStatusPick(true)}>Set Status First</Btn>
                  </div>
                </div>
              )}

              {!todayRecord && statusPick && (
                <div style={{ paddingTop:8 }}>
                  <p style={{ color:C.cream, fontSize:13, marginBottom:14 }}>Select your status:</p>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, maxWidth:320, margin:"0 auto" }}>
                    {TEACHER_STATUSES.map(s => (
                      <button key={s} onClick={()=>checkIn(s)} style={{
                        background:(STATUS_COLORS[s]||C.muted)+"22", color:STATUS_COLORS[s]||C.muted,
                        border:`1.5px solid ${(STATUS_COLORS[s]||C.muted)}55`,
                        borderRadius:12, padding:"11px 0", fontFamily:F.serif, fontWeight:700, fontSize:13, cursor:"pointer"
                      }}>{s}</button>
                    ))}
                  </div>
                  <div style={{ marginTop:12 }}><Btn variant="ghost" small onClick={()=>setStatusPick(false)}>Cancel</Btn></div>
                </div>
              )}

              {todayRecord && !isDone && (
                <div style={{ paddingTop:8 }}>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16, maxWidth:320, margin:"0 auto 16px" }}>
                    <div style={{ background:C.navy, borderRadius:12, padding:"14px" }}>
                      <p style={{ color:C.muted, fontSize:10, textTransform:"uppercase", letterSpacing:1, margin:"0 0 4px" }}>Checked In</p>
                      <p style={{ color:C.green, fontFamily:F.mono, fontSize:20, fontWeight:700, margin:0 }}>{todayRecord.arrivalTime}</p>
                    </div>
                    <div style={{ background:C.navy, borderRadius:12, padding:"14px" }}>
                      <p style={{ color:C.muted, fontSize:10, textTransform:"uppercase", letterSpacing:1, margin:"0 0 4px" }}>Departure</p>
                      <p style={{ color:C.muted, fontFamily:F.mono, fontSize:20, margin:0 }}>—</p>
                    </div>
                  </div>
                  <SBadge status={todayRecord.status} />
                  <div style={{ marginTop:16 }}>
                    <Btn variant="danger" onClick={checkOut} style={{ padding:"13px 32px" }}>Check Out — End of Day</Btn>
                  </div>
                </div>
              )}

              {isDone && (
                <div style={{ paddingTop:8 }}>
                  <SBadge status={todayRecord.status} />
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, margin:"14px auto", maxWidth:320 }}>
                    <div style={{ background:C.navy, borderRadius:12, padding:"12px" }}>
                      <p style={{ color:C.muted, fontSize:10, textTransform:"uppercase", letterSpacing:1, margin:"0 0 4px" }}>In</p>
                      <p style={{ color:C.green, fontFamily:F.mono, fontSize:18, fontWeight:700, margin:0 }}>{todayRecord.arrivalTime}</p>
                    </div>
                    <div style={{ background:C.navy, borderRadius:12, padding:"12px" }}>
                      <p style={{ color:C.muted, fontSize:10, textTransform:"uppercase", letterSpacing:1, margin:"0 0 4px" }}>Out</p>
                      <p style={{ color:C.gold, fontFamily:F.mono, fontSize:18, fontWeight:700, margin:0 }}>{todayRecord.departureTime}</p>
                    </div>
                  </div>
                  <p style={{ color:C.green, fontFamily:F.serif, fontSize:13, fontWeight:700 }}>Attendance complete for today.</p>
                </div>
              )}
            </Card>

            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:20 }}>
              {[
                { label:"In School",   val:inSchool.length,  color:C.green, bg:"#0a2518" },
                { label:"Picked Up",   val:pickedUp.length,  color:C.red,   bg:"#2a0e0e" },
                { label:"Late Pickup", val:lateKids.length,  color:C.warn,  bg:"#2a1500" },
              ].map(s=>(
                <div key={s.label} style={{ background:s.bg, borderRadius:14, padding:"14px 10px", textAlign:"center", border:`1px solid ${s.color}22` }}>
                  <p style={{ color:s.color, fontFamily:F.serif, fontSize:26, fontWeight:700, margin:0 }}>{s.val}</p>
                  <p style={{ color:C.muted, fontSize:10, textTransform:"uppercase", letterSpacing:1, margin:"4px 0 0" }}>{s.label}</p>
                </div>
              ))}
            </div>

            <SectionTitle>Staff On Duty Today</SectionTitle>
            {teachers.filter(t => { const r=t.attendance.find(a=>a.date===today); return r&&r.arrivalTime&&!r.departureTime; }).map(t=>{
              const r = t.attendance.find(a=>a.date===today);
              return (
                <div key={t.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 0", borderBottom:`1px solid ${C.gold}18` }}>
                  <span style={{ fontSize:22 }}>{t.emoji}</span>
                  <div style={{ flex:1 }}>
                    <p style={{ color:C.cream, fontFamily:F.serif, fontWeight:700, fontSize:13, margin:0 }}>{t.name}</p>
                    <p style={{ color:C.muted, fontSize:11, margin:"2px 0 0" }}>In: {r.arrivalTime}</p>
                  </div>
                  <span style={{ background:(STATUS_COLORS[r.status]||C.muted)+"22", color:STATUS_COLORS[r.status]||C.muted, borderRadius:20, padding:"3px 10px", fontSize:11, fontWeight:700, fontFamily:F.serif }}>{r.status}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* ══ ATTENDANCE ══ */}
        {view === "attendance" && (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <h3 style={{ color:C.gold, fontFamily:F.serif, fontSize:15, margin:0 }}>
                Attendance — {new Date().toLocaleDateString("en-ZA",{weekday:"long",day:"numeric",month:"long"})}
              </h3>
              <button onClick={() => {
                if (!window.confirm("Reset today's attendance for all children?")) return;
                children.forEach(child => {
                  const updated = { ...child, activityLog: { ...child.activityLog, [dateKey()]: (child.activityLog?.[dateKey()]||[]).filter(a=>!a.startsWith("ATTENDANCE:")) }};
                  onUpdateChild(updated);
                });
              }} style={{ background:"transparent", color:C.muted, border:`1px solid ${C.muted}44`, borderRadius:12, padding:"4px 10px", fontSize:10, fontFamily:F.serif, cursor:"pointer" }}>
                ↺ Reset Today
              </button>
            </div>
            {children.map(child => {
              const record = child.activityLog?.[dateKey()]?.find?.(a => a.startsWith("ATTENDANCE:"));
              const useNewFmt = record?.includes("|");
              const parts = record ? (useNewFmt ? record.split("|") : record.split(":")) : [];
              const status = useNewFmt ? parts[0]?.replace("ATTENDANCE:","") : parts[1] || null;
              const markedBy = parts[2] || null;
              const markedAt = useNewFmt ? (parts[3]||null) : (parts[3]&&parts[4] ? parts[3]+":"+parts[4] : parts[3]||null);
              return (
                <div key={child.id} style={{
                  background:C.navyL, borderRadius:14, padding:"14px 16px", marginBottom:10,
                  border:`1.5px solid ${status==="present" ? C.green : status==="absent" ? C.red : C.muted}33`,
                  display:"flex", alignItems:"center", justifyContent:"space-between"
                }}>
                  <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                    <span style={{ fontSize:26 }}>{child.photo}</span>
                    <div>
                      <p style={{ color:C.cream, fontFamily:F.serif, fontWeight:700, fontSize:14, margin:0 }}>{child.name}</p>
                      {status && <p style={{ color: status==="present" ? C.green : C.red, fontFamily:F.serif, fontSize:11, margin:"3px 0 0" }}>
                        {status === "present" ? "✅ Present" : "❌ Absent"} · {markedAt} · {markedBy}
                      </p>}
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    {!status && <>
                      <Btn small variant="green" onClick={() => {
                        const entry = `ATTENDANCE:present|${teacher.name}|${nowTime()}`;
                        const updated = { ...child, status:"at-school", activityLog: { ...child.activityLog, [dateKey()]: [...(child.activityLog?.[dateKey()]||[]).filter(a=>!a.startsWith("ATTENDANCE:")), entry] }};
                        onUpdateChild(updated);
                      }}>✅ Present</Btn>
                      <Btn small variant="danger" onClick={() => {
                        const entry = `ATTENDANCE:absent|${teacher.name}|${nowTime()}`;
                        const updated = { ...child, status:"absent", activityLog: { ...child.activityLog, [dateKey()]: [...(child.activityLog?.[dateKey()]||[]).filter(a=>!a.startsWith("ATTENDANCE:")), entry] }};
                        onUpdateChild(updated);
                      }}>❌ Absent</Btn>
                    </>}
                    {status && <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                      <span style={{ color: status==="present" ? C.green : C.red, fontSize:20 }}>
                        {status === "present" ? "✅" : "❌"}
                      </span>
                      <Btn small variant="ghost" onClick={() => {
                        const updated = { ...child, status:"unknown", activityLog: { ...child.activityLog, [dateKey()]: (child.activityLog?.[dateKey()]||[]).filter(a=>!a.startsWith("ATTENDANCE:")) }};
                        onUpdateChild(updated);
                      }}>↩ Undo</Btn>
                    </div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {view === "children" && (
          <div>
            <h3 style={{ color:C.gold, fontFamily:F.serif, fontSize:15, margin:"0 0 16px" }}>
              Children — {new Date().toLocaleDateString("en-ZA",{weekday:"long",day:"numeric",month:"long"})}
            </h3>
            {lateKids.length > 0 && (
              <div style={{ background:C.warn+"22", border:`2px solid ${C.warn}`, borderRadius:14, padding:"12px 16px", marginBottom:16, display:"flex", gap:12, alignItems:"center" }}>
                <span style={{ fontSize:20 }}>⚠️</span>
                <div>
                  <p style={{ color:C.warn, fontFamily:F.serif, fontWeight:700, fontSize:13, margin:0 }}>Late Pickup — {lateKids.length} child{lateKids.length>1?"ren":""}</p>
                  <p style={{ color:C.cream, fontFamily:F.serif, fontSize:11, margin:"3px 0 0" }}>{lateKids.map(c=>c.name.split(" ")[0]).join(", ")} not yet collected</p>
                </div>
              </div>
            )}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:18 }}>
              {[
                { label:"In School",  val:inSchool.length,  color:C.gold },
                { label:"Picked Up",  val:pickedUp.length,  color:C.red },
                { label:"Enrolled",   val:children.length,  color:C.muted },
              ].map(s=>(
                <div key={s.label} style={{ background:C.navyL, borderRadius:12, padding:"12px 8px", textAlign:"center" }}>
                  <p style={{ color:s.color, fontFamily:F.serif, fontSize:22, fontWeight:700, margin:0 }}>{s.val}</p>
                  <p style={{ color:C.muted, fontSize:10, textTransform:"uppercase", letterSpacing:1, margin:"3px 0 0" }}>{s.label}</p>
                </div>
              ))}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))", gap:10 }}>
              {children.map(child => (
                <div key={child.id} style={{
                  background:C.navyL, borderRadius:14, padding:"14px 16px",
                  border:`1.5px solid ${isLate(child) ? C.warn : (statusColor(child.status))}33`
                }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                    <span style={{ fontSize:26 }}>{child.photo}</span>
                    <div style={{ flex:1 }}>
                      <p style={{ color:C.cream, fontFamily:F.serif, fontWeight:700, fontSize:13, margin:0 }}>{child.name}</p>
                      <p style={{ color:C.muted, fontSize:11, margin:"2px 0 0" }}>{child.guardian}</p>
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    <Badge label={statusLabel(child.status)} color={statusColor(child.status)} />
                    {isLate(child) && <Badge label="Late!" color={C.warn} />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {view === "staff" && (
          <div>
            <h3 style={{ color:C.gold, fontFamily:F.serif, fontSize:15, margin:"0 0 16px" }}>
              All Staff — {new Date().toLocaleDateString("en-ZA",{weekday:"long",day:"numeric",month:"long"})}
            </h3>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:20 }}>
              {[
                { label:"Present",       val:teachers.filter(t=>{const r=t.attendance.find(a=>a.date===today);return r&&(r.status==="Present"||r.status==="Late");}).length, color:C.green },
                { label:"Absent / Sick", val:teachers.filter(t=>{const r=t.attendance.find(a=>a.date===today);return r&&(r.status==="Absent"||r.status==="Sick");}).length, color:C.red },
                { label:"Not Logged",    val:teachers.filter(t=>!t.attendance.find(a=>a.date===today)).length, color:C.muted },
              ].map(s=>(
                <div key={s.label} style={{ background:C.navyL, borderRadius:12, padding:"14px 10px", textAlign:"center", border:`1px solid ${s.color}22` }}>
                  <p style={{ color:s.color, fontFamily:F.serif, fontSize:24, fontWeight:700, margin:0 }}>{s.val}</p>
                  <p style={{ color:C.muted, fontSize:10, textTransform:"uppercase", letterSpacing:1, margin:"4px 0 0" }}>{s.label}</p>
                </div>
              ))}
            </div>
            {teachers.map(t => {
              const rec = t.attendance.find(a=>a.date===today);
              const isMe = t.id === teacher.id;
              return (
                <div key={t.id} style={{
                  background: isMe ? C.gold+"18" : C.navyL, borderRadius:14, padding:"14px 16px", marginBottom:10,
                  display:"flex", alignItems:"center", gap:12,
                  border:`1.5px solid ${isMe ? C.gold : rec ? (STATUS_COLORS[rec.status]||C.muted)+"33" : C.muted+"22"}`
                }}>
                  <span style={{ fontSize:26 }}>{t.emoji}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                      <p style={{ color:C.cream, fontFamily:F.serif, fontWeight:700, fontSize:13, margin:0 }}>{t.name}</p>
                      {isMe && <span style={{ color:C.gold, fontSize:10, fontFamily:F.serif }}>(you)</span>}
                    </div>
                    <p style={{ color:C.muted, fontSize:11, margin:"2px 0 0" }}>{t.role}</p>
                  </div>
                  {rec ? <span style={{ background:(STATUS_COLORS[rec.status]||C.muted)+"22", color:STATUS_COLORS[rec.status]||C.muted, borderRadius:20, padding:"3px 10px", fontSize:11, fontWeight:700, fontFamily:F.serif }}>{rec.status}</span> : <span style={{ color:C.muted, fontSize:11 }}>Not logged</span>}
                </div>
              );
            })}
          </div>
        )}

        {view === "my" && (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <h3 style={{ color:C.gold, fontFamily:F.serif, fontSize:15, margin:0 }}>My Attendance</h3>
              <Btn small variant="secondary" onClick={exportCSV}>⬇ Export CSV</Btn>
            </div>
            <RequestLeaveCard teacher={teacher} onUpdateTeachers={onUpdateTeachers} today={today} />
            {[...Array(7)].map((_,i) => {
              const d = new Date(); d.setDate(d.getDate()-i);
              const dk = d.toISOString().slice(0,10);
              const r = teacher.attendance.find(a=>a.date===dk);
              if (!r && i > 0) return null;
              return (
                <div key={dk} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 0", borderBottom:`1px solid ${C.gold}18` }}>
                  <div>
                    <p style={{ color: dk===today ? C.gold : C.cream, fontFamily:F.serif, fontWeight:700, fontSize:13, margin:0 }}>
                      {dk===today ? "Today" : d.toLocaleDateString("en-ZA",{weekday:"short",day:"numeric",month:"short"})}
                    </p>
                    {r ? (
                      <p style={{ color:C.muted, fontSize:11, fontFamily:F.mono, margin:"3px 0 0" }}>
                        {r.arrivalTime ? `${r.arrivalTime} → ${r.departureTime||"—"}` : `Logged: ${r.status}`}
                      </p>
                    ) : (
                      <p style={{ color:C.muted, fontSize:11, margin:"3px 0 0" }}>Not logged</p>
                    )}
                  </div>
                  {r ? <span style={{ background:(STATUS_COLORS[r.status]||C.muted)+"22", color:STATUS_COLORS[r.status]||C.muted, borderRadius:20, padding:"3px 10px", fontSize:11, fontWeight:700, fontFamily:F.serif }}>{r.status}</span> : <span style={{ color:C.muted, fontSize:11 }}>—</span>}
                </div>
              );
            }).filter(Boolean)}
          </div>
        )}

        {view === "history" && (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <h3 style={{ color:C.gold, fontFamily:F.serif, fontSize:15, margin:0 }}>Full Attendance History</h3>
              <Btn small variant="secondary" onClick={exportCSV}>⬇ Export CSV</Btn>
            </div>
            {(() => {
              const months = {};
              teacher.attendance.forEach(r => {
                const m = r.date.slice(0,7);
                if (!months[m]) months[m] = { present:0, late:0, absent:0, sick:0, leave:0, half:0 };
                if (r.status==="Present") months[m].present++;
                else if (r.status==="Late") months[m].late++;
                else if (r.status==="Absent") months[m].absent++;
                else if (r.status==="Sick") months[m].sick++;
                else if (r.status==="On Leave") months[m].leave++;
                else if (r.status==="Half Day") months[m].half++;
              });
              return Object.entries(months).sort((a,b)=>b[0].localeCompare(a[0])).map(([m,s])=>(
                <div key={m} style={{ background:C.navyL, borderRadius:14, padding:"14px 16px", marginBottom:10 }}>
                  <p style={{ color:C.gold, fontFamily:F.serif, fontWeight:700, fontSize:13, margin:"0 0 10px" }}>
                    {new Date(m+"-01").toLocaleDateString("en-ZA",{month:"long",year:"numeric"})}
                  </p>
                  <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
                    {[["Present",s.present,C.green],["Late",s.late,C.warn],["Absent",s.absent,C.red],["Sick",s.sick,C.orange],["On Leave",s.leave,"#4fc3f7"],["Half Day",s.half,C.gold]].map(([l,v,col])=>
                      v > 0 ? (
                        <div key={l} style={{ textAlign:"center" }}>
                          <p style={{ color:col, fontFamily:F.serif, fontSize:18, fontWeight:700, margin:0 }}>{v}</p>
                          <p style={{ color:C.muted, fontSize:10, margin:0 }}>{l}</p>
                        </div>
                      ) : null
                    )}
                  </div>
                </div>
              ));
            })()}
            {teacher.attendance.length === 0 && (
              <p style={{ color:C.muted, fontFamily:F.serif, fontSize:13 }}>No attendance history yet.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── PANIC GUARD ───────────────────────────────────────────────────────────────
function PanicGuard({ settings, onTriggered }) {
  const tapCount  = useRef(0);
  const tapTimer  = useRef(null);
  const triggered = useRef(false);

  const handleStarTap = useCallback(() => {
    tapCount.current += 1;
    if (tapTimer.current) clearTimeout(tapTimer.current);
    tapTimer.current = setTimeout(() => { tapCount.current = 0; }, 1200);
    if (tapCount.current >= 3) {
      tapCount.current = 0;
      if (!triggered.current) {
        triggered.current = true;
        onTriggered();
        setTimeout(() => { triggered.current = false; }, 10000);
      }
    }
  }, [onTriggered]);

  useEffect(() => {
    let buffer = "";
    const handleKey = (e) => {
      buffer += e.key.toUpperCase();
      if (buffer.length > 6) buffer = buffer.slice(-6);
      if (buffer.includes("HELP") && !triggered.current) {
        triggered.current = true;
        onTriggered();
        setTimeout(() => { triggered.current = false; }, 10000);
        buffer = "";
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onTriggered]);

  useEffect(() => {
    window.__nkanyeziStarTap = handleStarTap;
    return () => { delete window.__nkanyeziStarTap; };
  }, [handleStarTap]);

  return null;
}

// ── PANIC OVERLAY ─────────────────────────────────────────────────────────────
function PanicOverlay({ onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 5000); return () => clearTimeout(t); }, []);
  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(231,76,60,0.97)", zIndex:99999,
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
      fontFamily:F.serif, textAlign:"center", padding:32
    }}>
      <div style={{ fontSize:72, marginBottom:20 }}>🚨</div>
      <h1 style={{ color:"#fff", fontSize:28, fontWeight:700, margin:"0 0 12px" }}>EMERGENCY ALERT SENT</h1>
      <p style={{ color:"rgba(255,255,255,0.85)", fontSize:16, marginBottom:6 }}>WhatsApp alerts fired to all emergency contacts.</p>
      <div style={{ background:"rgba(255,255,255,0.15)", borderRadius:16, padding:"18px 28px", marginBottom:24, maxWidth:360 }}>
        <p style={{ color:"#fff", fontSize:14, fontWeight:700, margin:"0 0 8px" }}>Stay calm. Say this to the visitor:</p>
        <p style={{ color:"rgba(255,255,255,0.9)", fontSize:13, fontStyle:"italic", margin:0, lineHeight:1.8 }}>
          "I just need to finish this registration — one moment please."
        </p>
      </div>
      <p style={{ color:"rgba(255,255,255,0.45)", fontSize:11 }}>Screen returns to normal in 5 seconds</p>
    </div>
  );
}

// ── School Login Screen ───────────────────────────────────────────────────────
function SchoolLogin({ onLogin }) {
  const [code, setCode]   = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!code.trim()) { setError("Please enter your school code."); return; }
    setLoading(true);
    try {
      const ref = doc(db, "schools", code.trim().toLowerCase());
      const snap = await getDoc(ref);
      if (snap.exists()) {
        onLogin({ id: code.trim().toLowerCase(), ...snap.data() });
      } else {
        setError("School code not found. Contact Nkanyezi to register your school.");
      }
    } catch (e) {
      setError("Connection error. Please check your internet and try again.");
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:32, background:`radial-gradient(ellipse at 15% 15%, #1a2a5e 0%, #0D1B3E 55%, #060e1f 100%)` }}>
      <div style={{ width:"100%", maxWidth:360, textAlign:"center", fontFamily:F.serif }}>
        <span style={{ fontSize:56, display:"block", marginBottom:14 }}>⭐</span>
        <h1 style={{ color:C.gold, fontSize:26, fontWeight:700, margin:"0 0 8px" }}>Nkanyezi SafePickup</h1>
        <p style={{ color:C.muted, fontSize:13, marginBottom:40, letterSpacing:1 }}>SCHOOL PORTAL</p>

        <div style={{ background:C.navyL, borderRadius:20, padding:"28px 24px", border:`1.5px solid ${C.gold}33` }}>
          <p style={{ color:C.cream, fontFamily:F.serif, fontSize:15, marginBottom:20 }}>Enter your school code to continue</p>
          <input
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase())}
            placeholder="e.g. SAFE2026"
            onKeyDown={e => e.key === "Enter" && handleLogin()}
            style={{
              width:"100%", padding:"14px 16px", borderRadius:12,
              background:C.navy, border:`1.5px solid ${C.gold}55`,
              color:C.gold, fontFamily:F.mono, fontSize:20,
              letterSpacing:4, outline:"none", textAlign:"center",
              boxSizing:"border-box", marginBottom:16
            }}
          />
          {error && <p style={{ color:C.red, fontFamily:F.serif, fontSize:13, marginBottom:14 }}>{error}</p>}
          <Btn onClick={handleLogin} disabled={loading} style={{ width:"100%", padding:"14px 0", fontSize:15 }}>
            {loading ? "Connecting..." : "Enter School Portal →"}
          </Btn>
        </div>

        <p style={{ color:C.muted, fontSize:11, marginTop:24, lineHeight:1.8 }}>
          Don't have a school code?<br/>
          Contact <strong style={{color:C.gold}}>Nkanyezi Digital Skills Academy</strong><br/>
          +27 63 479 7549
        </p>
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen]         = useState("school-login");
  const [schoolData, setSchoolData] = useState(null);
  const [children, setChildren]     = useState([]);
  const [teachers, setTeachers]     = useState([]);
  const [activeTeacher, setActiveTeacher] = useState(null);
  const [toasts, setToasts]         = useState([]);
  const [staffErr, setStaffErr]     = useState("");
  const [settings, setSettings]     = useState(DEFAULT_SETTINGS);
  const [panicActive, setPanicActive] = useState(false);
  const [loading, setLoading]       = useState(false);

  const notify = (msg, type="info") => setToasts(ts => [...ts, { id:uid(), msg, type }]);
  const removeToast = id => setToasts(ts => ts.filter(t => t.id !== id));

  const loadSchool = async (school) => {
    setLoading(true);
    setSchoolData(school);
    const settingsSnap = await getDoc(doc(db, "schools", school.id, "config", "settings"));
    if (settingsSnap.exists()) setSettings({ ...DEFAULT_SETTINGS, ...settingsSnap.data() });
    else {
      const newSettings = { ...DEFAULT_SETTINGS, schoolName: school.name || school.id };
      await setDoc(doc(db, "schools", school.id, "config", "settings"), newSettings);
      setSettings(newSettings);
    }
    setLoading(false);
    setScreen("home");
  };

  useEffect(() => {
    if (!schoolData) return;
    const sid = schoolData.id;
    const unsubChildren = onSnapshot(
      collection(db, "schools", sid, "children"),
      snap => setChildren(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    const unsubTeachers = onSnapshot(
      collection(db, "schools", sid, "teachers"),
      snap => setTeachers(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return () => { unsubChildren(); unsubTeachers(); };
  }, [schoolData]);

  // ── Firebase CRUD ─────────────────────────────────────────────────────────
  const saveChild = async (child) => {
    const sid = schoolData.id;
    await setDoc(doc(db, "schools", sid, "children", child.id), child);
  };

  const deleteChild = async (id) => {
    await deleteDoc(doc(db, "schools", schoolData.id, "children", id));
  };

  const saveTeacher = async (teacher) => {
    await setDoc(doc(db, "schools", schoolData.id, "teachers", teacher.id), teacher);
  };

  const deleteTeacher = async (id) => {
    await deleteDoc(doc(db, "schools", schoolData.id, "teachers", id));
  };

  const saveSettings = async (newSettings) => {
    setSettings(newSettings);
    await setDoc(doc(db, "schools", schoolData.id, "config", "settings"), newSettings);
    notify("Settings saved!");
  };

  // ── KEY FIX: updateChild correctly saves to Firebase and updates state ──
  const updateChild = async (updated) => {
    setChildren(cs => cs.map(c => c.id === updated.id ? updated : c));
    await saveChild(updated);
  };

  const addActivity = async (id, note) => {
    const child = children.find(c => c.id === id);
    if (!child) return;
    const d = dateKey();
    const updated = { ...child, activityLog: { ...child.activityLog, [d]: [...(child.activityLog[d]||[]), note] } };
    await saveChild(updated);
  };

  const handlePanic = useCallback(() => {
    firePanicAlert(settings);
    setPanicActive(true);
  }, [settings]);

  useEffect(() => {
    if (!schoolData || children.length === 0) return;
    const { cleaned, purgedCount } = purgeOldPhotos(children);
    if (purgedCount > 0) {
      cleaned.forEach(c => saveChild(c));
      notify(`🗑 ${purgedCount} old photo${purgedCount>1?"s":""} removed. Records kept.`);
    }
  }, [schoolData]);

  useEffect(() => {
    const onManualPurge = (e) => {
      e.detail.cleaned.forEach(c => saveChild(c));
      if (e.detail.purgedCount > 0) notify(`🗑 Manual clean-up: ${e.detail.purgedCount} photo${e.detail.purgedCount>1?"s":""} removed.`);
    };
    window.addEventListener("nkanyezi-purge", onManualPurge);
    return () => window.removeEventListener("nkanyezi-purge", onManualPurge);
  }, [schoolData]);

  const Stars = () => (
    <div style={{ position:"fixed", inset:0, pointerEvents:"none", zIndex:0, overflow:"hidden" }}>
      {[...Array(22)].map((_,i) => (
        <div key={i} style={{
          position:"absolute", left:`${(i*43+17)%100}%`, top:`${(i*61+9)%100}%`,
          width:i%4===0?3:2, height:i%4===0?3:2, borderRadius:"50%",
          background:C.gold, opacity:0.1+(i%6)*0.04
        }} />
      ))}
    </div>
  );

  return (
    <div style={{ minHeight:"100vh", background:`radial-gradient(ellipse at 15% 15%, #1a2a5e 0%, ${C.navy} 55%, ${C.navyD} 100%)`, color:C.cream, position:"relative", overflowX:"hidden" }}>

      <PanicGuard settings={settings} onTriggered={handlePanic} />
      {panicActive && <PanicOverlay onDone={() => setPanicActive(false)} />}

      <Stars />
      <div style={{ position:"relative", zIndex:1 }}>

        {screen === "school-login" && <SchoolLogin onLogin={school => loadSchool(school)} />}

        {loading && (
          <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:48, animation:"spin 1s linear infinite", display:"inline-block" }}>⭐</div>
              <p style={{ color:C.gold, fontFamily:F.serif, marginTop:16 }}>Loading your school...</p>
            </div>
          </div>
        )}

        {screen === "home" && (
          <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:32, textAlign:"center" }}>
            <div style={{ fontSize:64, marginBottom:14, cursor:"default", userSelect:"none" }} onClick={() => window.__nkanyeziStarTap?.()}>⭐</div>
            <h1 style={{ color:C.gold, fontSize:30, fontWeight:700, fontFamily:F.serif, margin:"0 0 8px" }}>Nkanyezi SafePickup</h1>
            <p style={{ color:C.muted, fontSize:14, marginBottom:6, fontFamily:F.serif }}>Secure school safety & management system</p>
            <p style={{ color:C.muted, fontSize:11, marginBottom:48, letterSpacing:1 }}>Powered by Nkanyezi Digital Skills Academy</p>
            <div style={{ display:"flex", flexDirection:"column", gap:14, width:"100%", maxWidth:320 }}>
              <Btn onClick={() => setScreen("parent")} style={{ padding:"18px 0", fontSize:16 }}>👨‍👩‍👧 I'm a Parent / Guardian</Btn>
              <Btn variant="secondary" onClick={() => setScreen("staff-login")} style={{ padding:"18px 0", fontSize:16 }}>🏫 School Staff Login</Btn>
              <button onClick={() => setScreen("appointments")} style={{
                background:"transparent", border:`2px solid ${C.gold}66`,
                borderRadius:14, padding:"15px 0", fontSize:15,
                fontFamily:F.serif, fontWeight:700, cursor:"pointer",
                color:C.gold, marginTop:4, transition:"background 0.2s"
              }}
              onMouseEnter={e=>e.currentTarget.style.background=C.gold+"18"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}
              >📅 Appointments</button>
              <button onClick={() => setScreen("visitor")} style={{
                background:"transparent", border:`2px solid ${C.red}66`,
                borderRadius:14, padding:"15px 0", fontSize:15,
                fontFamily:F.serif, fontWeight:700, cursor:"pointer",
                color:C.red, marginTop:4, transition:"background 0.2s"
              }}
              onMouseEnter={e=>e.currentTarget.style.background=C.red+"18"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}
              >👤 Log a Visitor</button>
            </div>
          </div>
        )}

        {screen === "staff-login" && (
          <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:32, textAlign:"center" }}>
            <h2 style={{ color:C.gold, fontFamily:F.serif, marginBottom:8, fontSize:20 }}>Staff Access</h2>
            <p style={{ color:C.muted, fontSize:12, marginBottom:32, letterSpacing:1 }}>Who is logging in?</p>
            <div style={{ display:"flex", flexDirection:"column", gap:12, width:"100%", maxWidth:300, marginBottom:24 }}>
              <Btn onClick={() => setScreen("principal-login")} style={{ padding:"16px 0", fontSize:15 }}>🏫 Principal / Admin</Btn>
              <Btn variant="secondary" onClick={() => setScreen("teacher-login")} style={{ padding:"16px 0", fontSize:15 }}>👩‍🏫 I am a Teacher</Btn>
            </div>
            <Btn variant="ghost" small onClick={() => setScreen("home")}>← Back</Btn>
          </div>
        )}

        {screen === "principal-login" && (
          <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:32 }}>
            <h2 style={{ color:C.gold, fontFamily:F.serif, marginBottom:8, fontSize:20 }}>Principal Login</h2>
            <p style={{ color:C.muted, fontSize:12, marginBottom:32, letterSpacing:1 }}>Enter your admin PIN</p>
            <PinPad label="Admin PIN" alphanumeric
              onSubmit={pin => {
                const adminPin = settings.adminPin || STAFF_PIN;
                if(pin===adminPin){setScreen("dashboard");setStaffErr("");}
                else setStaffErr("Incorrect PIN. Please try again.");
              }}
              onBack={() => { setScreen("staff-login"); setStaffErr(""); }} />
            {staffErr && <p style={{ color:C.red, marginTop:14, fontFamily:F.serif, fontSize:13 }}>{staffErr}</p>}
          </div>
        )}

        {screen === "teacher-login" && (
          <TeacherLogin
            teachers={teachers}
            onSuccess={t => { setActiveTeacher(t); setScreen("teacher-dashboard"); }}
            onBack={() => setScreen("staff-login")}
          />
        )}

        {screen === "teacher-dashboard" && activeTeacher && (
          <TeacherDashboard
            teacher={teachers.find(t => t.id === activeTeacher.id) || activeTeacher}
            teachers={teachers}
            children={children}
            settings={settings}
            onLogout={() => { setActiveTeacher(null); setScreen("home"); }}
            onUpdateTeachers={async updated => {
              setTeachers(ts => ts.map(t => t.id === updated.id ? updated : t));
              setActiveTeacher(updated);
              await saveTeacher(updated);
            }}
            onUpdateChild={updateChild}
          />
        )}

        {screen === "dashboard" && (
          <Dashboard
            children={children}
            teachers={teachers}
            onLogout={() => setScreen("home")}
            onAdminClick={() => setScreen("admin")}
            onAddActivity={addActivity}
            onUpdateChild={updateChild}
            settings={settings}
            onUpdateTeacher={async updated => {
              setTeachers(ts => ts.map(t => t.id === updated.id ? updated : t));
              await saveTeacher(updated);
            }}
          />
        )}

        {screen === "parent" && (
          <ParentFlow
            children={children}
            settings={settings}
            onUpdate={c => { updateChild(c); }}
            onBack={() => setScreen("home")}
            onToast={notify}
          />
        )}

        {screen === "admin" && (
          <AdminPanel
            children={children}
            teachers={teachers}
            onAddTeacher={async t => { await saveTeacher(t); notify(`${t.name} added.`); }}
            onDeleteTeacher={async id => { await deleteTeacher(id); notify("Teacher removed."); }}
            settings={settings}
            onSaveSettings={saveSettings}
            onAdd={async c => { await saveChild(c); notify(`${c.name} added.`); }}
            onDelete={async id => { await deleteChild(id); notify("Child removed."); }}
            onBack={() => setScreen("dashboard")}
          />
        )}

        {screen === "visitor" && (
          <VisitorLog
            onBack={() => setScreen("home")}
            schoolName={settings.schoolName}
            schoolWhatsApp={settings.schoolGroupLink || settings.schoolPhone}
            onToast={notify}
          />
        )}

        {screen === "appointments" && (
          <AppointmentsScreen
            onBack={() => setScreen("home")}
            settings={settings}
            staffName="Staff"
            teachers={teachers}
          />
        )}
      </div>

      {toasts.slice(-1).map(t => <Toast key={t.id} msg={t.msg} type={t.type} onDone={() => removeToast(t.id)} />)}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&display=swap');
        * { box-sizing: border-box; }
        @keyframes fadeUp { from{opacity:0;transform:translate(-50%,20px)}to{opacity:1;transform:translate(-50%,0)} }
        @keyframes spin { from{transform:rotate(0deg)}to{transform:rotate(360deg)} }
        ::-webkit-scrollbar{width:6px} ::-webkit-scrollbar-thumb{background:#D4A01744;border-radius:3px}
        input[type=time]::-webkit-calendar-picker-indicator{filter:invert(0.5)}
      `}</style>
    </div>
  );
}
